import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import usePreWarmedMic from "@/hooks/usePreWarmedMic";
import useVoiceEnergy from "@/hooks/useVoiceEnergy";
import useVoiceState, { STATES } from "@/hooks/useVoiceState";
import {
  computeAdaptiveSilenceTimeout,
  computeTieredSilenceTimeout,
  countWords,
  appendToAccumulator,
  looksLikeTtsEcho,
  isScopeAnswerAllowlisted,
  readVadSilenceBaseFromEnv,
  readVadSilenceBonusMaxFromEnv,
} from "@/hooks/_voiceUtils";
import {
  recordBargeInEvent,
  markLastEventFollowUp,
} from "@/hooks/_voiceTelemetry";
import { playListenChime, playReceivedChime, ensureChimesReady } from "@/utils/chimes";
import { isIOS } from "@/utils/platform";
// TELEMETRIA-S1 — trace events de voz (conversation_start, input_dropped,
// first_utterance_ready, tts.invoke, tts.first_audio_frame, tts.finish).
import trace from "@/services/executionTrace";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * useJarvisVoice — voice assistant orchestrator (VOICE-FIX-V2).
 *
 * Architecture:
 *   - State machine via useVoiceState (IDLE | LISTENING | PROCESSING |
 *     SPEAKING). Guards block illegal jumps (e.g. SPEAKING → LISTENING is
 *     only valid as audio_ended or barge_in).
 *   - Recognition runs in continuous mode; commit to PROCESSING is driven
 *     by an adaptive silence timer in this file, NOT by Chrome's `isFinal`.
 *     See computeAdaptiveSilenceTimeout for the formula.
 *   - During SPEAKING the mic track is hardware-muted via track.enabled=false
 *     and recognition is .abort()ed (drops buffered partials, unlike .stop()
 *     which preserves them and emits ghost transcripts after unmute).
 *   - Re-listen is gated on audio.onended + 500ms drain (not a fixed 3s).
 *     Fallback safety timeout 8s if onended never fires.
 *   - Barge-in: usePreWarmedMic runs in "bargein" mode while SPEAKING. A
 *     sustained voice above the elevated threshold kills TTS and jumps
 *     SPEAKING → LISTENING.
 */
export default function useJarvisVoice({ onVoiceInput, chatLoadingRef, authToken = null }) {
  // ====================== UI STATE ======================
  const [voiceOverlayOpen, setVoiceOverlayOpen] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [isConversationActive, setIsConversationActive] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nexo_voice_enabled") ?? "true"); } catch { return true; }
  });
  const [wakeWordEnabled, setWakeWordEnabled] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nexo_wakeword_enabled") ?? "false"); } catch { return false; }
  });
  const [wakeWordListening, setWakeWordListening] = useState(false);

  // ====================== STATE MACHINE ======================
  const voiceState = useVoiceState({
    onInvalid: ({ from, to, reason, message }) => {
      // Telemetry hook — kept lightweight (console only) until /api/voice/telemetry
      // lands. The error is also visible to the user via console.
      console.warn(`[nexo-voice] invalid_transition`, { from, to, reason, message });
    },
  });

  // FIX iOS — useVoiceEnergy expõe connectAudio/disconnectAudio que
  // roteiam o HTMLAudioElement pelo AudioContext.destination. Em iOS
  // Safari isso troca o canal do ringer (silent switch + volume baixo)
  // pro canal de mídia (volume dos botões). Sem isso, TTS sai inaudível.
  const { connectAudio, disconnectAudio } = useVoiceEnergy();

  // Bridge to legacy public API: NectarDashboard reads conversationStatus and
  // calls setConversationStatus. We expose the state value directly and a
  // setter that routes through transition() for guard-respecting behaviour.
  const conversationStatus = voiceState.state;
  const setConversationStatus = useCallback((nextStatus, reason = "external") => {
    // External callers may set "idle" as an emergency bail-out — accept via
    // force() so we always land on IDLE.
    if (nextStatus === STATES.IDLE) return voiceState.force(STATES.IDLE, reason);
    return voiceState.transition(nextStatus, reason);
  }, [voiceState]);

  // ====================== REFS ======================
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const wakeWordRecRef = useRef(null);
  const overlayOpenRef = useRef(false);
  const handleInputRef = useRef(null);
  const ttsAbortRef = useRef(null);
  const lastSentRef = useRef({ text: "", time: 0, greetCount: 0 });
  const ttsSafetyTimerRef = useRef(null);
  const reListenSafetyTimerRef = useRef(null);

  const voiceTimingsRef = useRef({});
  const speakIdRef = useRef(0);

  // FIX iOS STT (doc DIAGNOSTICO_VOICE_IOS §3): webkitSpeechRecognition é
  // stub em Safari iOS. Path alternativo via MediaRecorder + Whisper.
  const mediaRecorderRef = useRef(null);
  const mediaRecorderChunksRef = useRef([]);
  const transcribePendingRef = useRef(false);
  // FIX iOS STT robustez: contadores VAD + status do último transcribe
  // expostos no debug panel pra owner ver estado em tempo real.
  const iosVadStartsRef = useRef(0);
  const iosVadEndsRef = useRef(0);
  const iosLastTranscribeRef = useRef({ status: "none", ts: 0, chars: 0 });
  const iosRecorderInitStatusRef = useRef("idle");
  // Rastreia a última tentativa de listenOnce / scheduleRestart pra
  // identificar EXATAMENTE qual flag bloqueia quando o listenOnce não roda.
  // Diagnostica casos em que VAD captura voz mas Recorder fica em "idle".
  const lastListenAttemptRef = useRef({ where: "none", reason: "", ts: 0, retries: 0 });

  // ====================== PUSH-TO-TALK (iOS) ======================
  // Mic só grava enquanto o user SEGURA o botão. Substitui auto-listen
  // em iOS (que era vulnerável a auto-eco/hallucination do Whisper em
  // silêncio). Recorder independente do mediaRecorderRef pra não conflitar
  // com auto-listen do desktop. Sem VAD, sem silence timer, sem
  // scheduleRestart loop — gesto explícito controla início e fim.
  const [pushToTalkRecording, setPushToTalkRecording] = useState(false);
  const pttRecorderRef = useRef(null);
  const pttChunksRef = useRef([]);
  const pttMimeTypeRef = useRef("");
  const pttHoldStartRef = useRef(0);
  const pttCanceledRef = useRef(false);
  const PTT_MIN_HOLD_MS = 300;

  // FIX iOS — autoplay policy + Web Audio path.
  //
  // HTMLAudio.play() em iOS Safari rejeita quando o gesture do user (toggleVoice,
  // mic click, attach click) já expirou (>5s). Isso quebra TTS conversacional
  // que chega 2-4s após user falar (Whisper + LLM + ElevenLabs latency).
  //
  // Solução: warmupAudio ALSO cria/resume AudioContext. Em iOS, speakResponse
  // usa Web Audio (decodeAudioData + BufferSource + GainNode) que NÃO sofre
  // de autoplay porque AudioContext mantém gesture state uma vez resumido.
  //
  // Trade-off em iOS: perde streaming chunked (decodeAudioData precisa buffer
  // completo). Aceitável porque iOS já caía no path V4 blob (MediaSource MP3
  // não é suportado em iOS Safari).
  const audioWarmupDoneRef = useRef(false);
  const ttsAudioCtxRef = useRef(null);          // AudioContext do Web Audio TTS
  const currentTTSSourceRef = useRef(null);     // BufferSource ativa pra killTTS
  // Timestamp (Date.now()) até quando o TTS BufferSource está realmente
  // tocando. Set em source.start(0) com Date.now()+duration, clear em
  // source.onended e killTTS. Usado por startMediaRecorderListen e
  // commitUtterance pra recusar capturar/comitar enquanto TTS rola, mesmo
  // se voiceState saiu de SPEAKING por race do safety timer (decode lento).
  const ttsAudioPlayingUntilRef = useRef(0);
  const warmupAudio = useCallback(() => {
    // (1) AudioContext lazy init — UNICO no user gesture; resume desbloqueia
    // autoplay perpetuamente (gesture state preservado por AudioContext).
    try {
      if (!ttsAudioCtxRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          ttsAudioCtxRef.current = new AudioCtx();
          console.log("[nexo-voice] TTS AudioContext criado");
        }
      }
      if (ttsAudioCtxRef.current && ttsAudioCtxRef.current.state === "suspended") {
        ttsAudioCtxRef.current.resume().then(() => {
          console.log("[nexo-voice] TTS AudioContext resumed");
        }).catch((err) => {
          console.warn("[nexo-voice] TTS AudioContext resume falhou:", err);
        });
      }
    } catch (e) {
      console.warn("[nexo-voice] TTS AudioContext init falhou:", e);
    }

    // (2) HTMLAudioElement silent WAV warmup (mantido pra desktop que
    // continua usando HTMLAudio path).
    if (audioWarmupDoneRef.current) return;
    try {
      // 44 bytes WAV header com 0 amostras — silêncio puro.
      const silentWav = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAVFYAAFRWAAABAAgAZGF0YQAAAAA=";
      const a = new Audio(silentWav);
      a.volume = 0.001;
      a.playsInline = true;
      a.setAttribute("webkit-playsinline", "");
      const p = a.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          audioWarmupDoneRef.current = true;
          console.log("[nexo-voice] iOS audio warmup OK");
        }).catch((err) => {
          console.warn("[nexo-voice] iOS audio warmup falhou (ok ignorar):", err?.name);
        });
      }
    } catch (e) {
      console.warn("[nexo-voice] warmupAudio threw:", e);
    }
  }, []);

  // VOICE-POLISH-V8 — telemetria de finish reason do TTS pra debug panel.
  // Populado dentro de speakResponse.finish() em todos os caminhos
  // (ended/barge_in/abort/timeout/error). Exibido na linha "Last TTS
  // finish" do VoiceDebugPanel pra rastrear regressões visuais.
  const lastTtsFinishReasonRef = useRef(null);
  const lastTtsFinishAtRef = useRef(0);

  // VOICE-C1 F3 — Strict mute controller.
  // V2 fired a bare `setTimeout(500)` for re-enable — uncancellable, so two
  // consecutive TTS turns could race and the second's unmute could land on
  // top of the first's mute window. F3 keeps the timer in a ref, so:
  //   - a fresh speakResponse() can cancel any pending re-enable
  //   - barge-in cancels the pending re-enable before its own unmute
  //   - we never accidentally unmute while a new SPEAKING segment is active
  const micReEnableTimerRef = useRef(null);
  const cancelMicReEnable = () => {
    if (micReEnableTimerRef.current) {
      clearTimeout(micReEnableTimerRef.current);
      micReEnableTimerRef.current = null;
    }
  };
  const scheduleMicReEnable = (delay, reason) => {
    cancelMicReEnable();
    micReEnableTimerRef.current = setTimeout(() => {
      micReEnableTimerRef.current = null;
      console.log(`[nexo-voice] mic_reenable (${reason}) after ${delay}ms`);
      micApiRef.current.setBargeInMode?.("normal");
      micApiRef.current.setMicEnabled?.(true);
    }, delay);
  };

  // Master lock — true whenever recognition must NOT run (PROCESSING or
  // SPEAKING). Synchronously updated; useVoiceState mirror.
  const pipelineLockedRef = useRef(false);
  useEffect(() => {
    pipelineLockedRef.current =
      conversationStatus === STATES.PROCESSING ||
      conversationStatus === STATES.SPEAKING ||
      conversationStatus === STATES.CONNECTING;
  }, [conversationStatus]);

  const listenOnceFnRef = useRef(null);
  const restartTimerRef = useRef(null);
  // FIX iOS STT: refs pra arm/cancelSilenceTimer chamados dos callbacks
  // VAD do usePreWarmedMic. Não dá pra referenciar diretamente porque
  // armSilenceTimer (linha 838) é declarado DEPOIS do usePreWarmedMic
  // (linha 393) — TDZ. useEffect popula depois do mount, callbacks VAD
  // chamam via .current.
  const armSilenceTimerFnRef = useRef(null);
  const cancelSilenceTimerFnRef = useRef(null);

  // VOICE-INPUT-DEAD — telemetria de drops silenciosos. Preenchido em
  // useEffect abaixo apontando pra recordVoiceDrop em _voiceTelemetry.
  // Usado por commitUtterance/listenOnce via optional-chain.
  const recordVoiceDropRef = useRef(null);

  // VOICE-INPUT-DEAD fix 4 — contador interno de drops por sessão.
  // - total: contador acumulado pra debug (__nexoVoiceDebug)
  // - consecutive: zerado em todo commit OK; >=2 dispara toast
  // - lastReason/lastAt: pra exibição opcional no overlay
  const voiceDropCountRef = useRef({
    total: 0,
    consecutive: 0,
    lastReason: null,
    lastAt: 0,
  });

  // Adaptive silence timer state — owned in this file, not the recognition lib.
  const accumulatedFinalRef = useRef("");
  const interimTextRef = useRef("");
  const silenceTimerRef = useRef(null);
  const silenceBaseRef = useRef(readVadSilenceBaseFromEnv());
  const silenceBonusMaxRef = useRef(readVadSilenceBonusMaxFromEnv());

  // VOICE-UNIFICAR — rolling buffer of recent TTS text used to detect echo.
  // Speaker bleed makes the STT engine transcribe fragments of Nexo's own
  // reply (Web Speech API listens via OS mic, not our muted MediaStream).
  // We compare new transcripts against this buffer in commitUtterance and
  // drop matches. Buffer auto-expires after RECENT_TTS_WINDOW_MS so an
  // older reply doesn't keep filtering legit follow-up questions.
  const recentTtsRef = useRef({ text: "", time: 0 });
  const RECENT_TTS_WINDOW_MS = 30000;
  const rememberTtsText = (text) => {
    if (!text) return;
    const now = Date.now();
    // If the last TTS is fresh, append (cobre pre-fetch streaming que faz
    // várias speakResponse seguidas). Senão substitui.
    if (now - recentTtsRef.current.time < RECENT_TTS_WINDOW_MS) {
      recentTtsRef.current = {
        text: `${recentTtsRef.current.text} ${text}`.trim(),
        time: now,
      };
    } else {
      recentTtsRef.current = { text, time: now };
    }
  };
  const isRecentTtsEcho = (raw) => {
    const { text, time } = recentTtsRef.current;
    if (!text) return false;
    if (Date.now() - time > RECENT_TTS_WINDOW_MS) return false;
    return looksLikeTtsEcho(raw, text);
  };

  // VOICE-UNIFICAR — Quiet period post audio.onended.
  // Defense-in-depth complementing the content-based echo filter: speakers
  // have internal buffers (especially bluetooth, ~100-300ms typical) and
  // the OS mic continues to pick up residual TTS audio for a beat after
  // audio.pause() / onended. STT rearma 550ms after onended (V2 default),
  // so any transcript that commits in the first ~Ns after the last TTS
  // can be speaker bleed, not the user. Drop it (the content-based echo
  // filter catches longer-window cases).
  //
  // REFINO-pos-a05eb3a (2026-06-02): 4000 → 1500. Auditoria mostrou que
  // 4s descartava respostas legítimas de clarificação ("a semana inteira"
  // dita 2s após Nexo perguntar "...ou a semana inteira?"). 1500ms cobre
  // o buffer de speaker e ainda deixa o user responder rápido.
  const lastTtsEndedAtRef = useRef(0);
  const POST_TTS_QUIET_MS = 1500;
  const isWithinPostTtsQuiet = () =>
    Date.now() - lastTtsEndedAtRef.current < POST_TTS_QUIET_MS;

  // REFINO-pos-a05eb3a: helpers extraídos pra _voiceUtils
  // (isScopeAnswerAllowlisted) — testáveis por unit test.

  const cancelPendingRestart = () => {
    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
  };
  const cancelSilenceTimer = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };
  const cancelReListenSafety = () => {
    if (reListenSafetyTimerRef.current) {
      clearTimeout(reListenSafetyTimerRef.current);
      reListenSafetyTimerRef.current = null;
    }
  };

  const scheduleRestart = (delay, reason) => {
    cancelPendingRestart();
    // FIX iOS STT: gate antes era silencioso — se falhava, listenOnce
    // nunca rodava E nada era logado. Owner ficava com state=LISTENING
    // mas Recorder=null porque scheduleRestart silenciosamente abandonava.
    // Agora: log explícito do motivo + retry curto pra race da useEffect
    // que mirra conversationStatus -> pipelineLockedRef (commit cycle).
    const attemptListen = (retries) => {
      const overlay = overlayOpenRef.current;
      const locked = pipelineLockedRef.current;
      const loading = chatLoadingRef?.current;
      if (!locked && overlay && !loading) {
        console.log(`[nexo-voice] restart (${reason}) after ${delay}ms retries=${retries}`);
        lastListenAttemptRef.current = { where: "scheduleRestart", reason: "ok", ts: Date.now(), retries };
        listenOnceFnRef.current?.();
        return;
      }
      const failReason = !overlay ? "overlay_closed"
        : locked ? `locked_${voiceState.get()}`
        : loading ? "chat_loading"
        : "unknown";
      console.warn(`[nexo-voice] scheduleRestart gate falhou: reason=${failReason} (parent=${reason}, retries=${retries})`);
      lastListenAttemptRef.current = { where: "scheduleRestart", reason: failReason, ts: Date.now(), retries };
      // Retry curto cobre race React commit cycle (useEffect que mirra
      // conversationStatus -> pipelineLockedRef leva ~16ms em geral).
      // 3 tentativas de 200ms cobrem até 600ms de gap.
      if (retries < 3) {
        restartTimerRef.current = setTimeout(() => {
          restartTimerRef.current = null;
          attemptListen(retries + 1);
        }, 200);
      } else {
        try {
          trace.record("voice.input_dropped", {
            path: "schedule_restart_gate",
            reason: failReason,
          });
        } catch (_) {}
      }
    };
    restartTimerRef.current = setTimeout(() => {
      restartTimerRef.current = null;
      attemptListen(0);
    }, delay);
  };

  const wakeWordSuppressedRef = useRef(false);
  const conversationStartedAtRef = useRef(0);

  const chimesEnabledRef = useRef(true);
  const greetingEnabledRef = useRef(true);
  const userIdRef = useRef(null);

  const refreshPrefs = useCallback(async () => {
    if (!authToken) return;
    try {
      let uid = userIdRef.current;
      if (!uid) {
        const me = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!me.ok) return;
        uid = (await me.json())?.id;
        if (!uid) return;
        userIdRef.current = uid;
      }
      const pr = await fetch(`${API_URL}/api/preferences?user_id=${encodeURIComponent(uid)}`);
      if (!pr.ok) return;
      const prefs = await pr.json();
      chimesEnabledRef.current = prefs?.chimes_enabled !== false;
      greetingEnabledRef.current = prefs?.greeting_enabled !== false;
    } catch (_) { /* default ok */ }
  }, [authToken]);

  useEffect(() => { refreshPrefs(); }, [refreshPrefs]);

  // ====================== PRE-WARM + VAD ======================
  // Forward ref so we can call setBargeInMode/setMicEnabled from inside
  // callbacks defined before the hook returns them.
  const micApiRef = useRef({});

  // VOICE-C1 F5 — telemetry follow-up: after a barge-in we wait up to
  // BARGE_IN_VERDICT_MS for an actual transcript. If one arrives,
  // commitUtterance() flips `pendingBargeInVerdictRef` to `true` (real
  // user cut-in). If the window expires without a transcript we mark
  // `false` (false positive — background noise tripped the threshold).
  const pendingBargeInVerdictRef = useRef(false);
  const bargeInVerdictTimerRef = useRef(null);
  const BARGE_IN_VERDICT_MS = 5000;

  const clearBargeInVerdictTimer = () => {
    if (bargeInVerdictTimerRef.current) {
      clearTimeout(bargeInVerdictTimerRef.current);
      bargeInVerdictTimerRef.current = null;
    }
  };

  const handleBargeIn = useCallback((meta = {}) => {
    // Fired by usePreWarmedMic when sustained voice above the elevated
    // threshold is detected during SPEAKING. Drop TTS, unmute, jump back
    // into LISTENING.
    if (voiceState.get() !== STATES.SPEAKING) return;
    console.log("[nexo-voice] barge_in_detected", { thresholdUsed: meta?.thresholdUsed });
    // VOICE-UNIFICAR — abrupt audio.pause() NAO dispara audio.onended.
    // Sem marcar o quiet period aqui, o STT que rearma 50ms depois pode
    // capturar audio residual do speaker e gerar transcript "fake".
    lastTtsEndedAtRef.current = Date.now();
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch (_) {}
    }
    if (ttsSafetyTimerRef.current) { clearTimeout(ttsSafetyTimerRef.current); ttsSafetyTimerRef.current = null; }
    if (ttsAbortRef.current) { try { ttsAbortRef.current.abort(); } catch (_) {} ttsAbortRef.current = null; }
    cancelReListenSafety();
    // F3 — cancel any pending mic-reenable timer before doing our own
    // immediate unmute (user is already speaking, drain delay is moot).
    cancelMicReEnable();
    // Drop any pre-fetched chunks queued behind the current TTS — user
    // already cut in, the queued continuation is no longer relevant.
    ttsQueueRef.current = [];
    micApiRef.current.setBargeInMode?.("normal");
    micApiRef.current.setMicEnabled?.(true);
    voiceState.transition(STATES.LISTENING, "barge_in");
    // Restart recognition immediately — user is already speaking.
    scheduleRestart(50, "barge_in");

    // VOICE-C1 F5 — record event with pending verdict, schedule timeout.
    try {
      recordBargeInEvent({
        thresholdUsed: meta?.thresholdUsed,
        followedByUserSpeech: null,
      });
    } catch (_) {}
    pendingBargeInVerdictRef.current = true;
    clearBargeInVerdictTimer();
    bargeInVerdictTimerRef.current = setTimeout(() => {
      bargeInVerdictTimerRef.current = null;
      if (pendingBargeInVerdictRef.current) {
        pendingBargeInVerdictRef.current = false;
        try { markLastEventFollowUp(false); } catch (_) {}
        console.log("[nexo-voice] barge_in_verdict false_positive");
      }
    }, BARGE_IN_VERDICT_MS);
  }, [voiceState]);

  const {
    isSpeaking: vadSpeaking,
    ensurePrewarmed,
    teardown: teardownMic,
    state: prewarmState,
    isOutputSpeaker,
    setMicEnabled,
    setBargeInMode,
    getMicTrack,
    // VOICE-FIX-WARMUP — grace period após audio.play() onde barge-in
    // detection fica suprimida (transiente alto do TTS arrancando).
    markSpeakSegmentStart,
    clearSpeakSegmentStart,
    getSpeakWarmupRemaining,
  } = usePreWarmedMic({
    onVoiceStart: () => {
      const t0 = voiceTimingsRef.current.t0;
      if (t0) {
        const t4 = performance.now();
        console.log(`[nexo-voice] T4 VAD speech-start (+${(t4 - t0).toFixed(0)}ms)`);
        voiceTimingsRef.current.t4 = t4;
      }
      // FIX iOS STT: em iOS, rec.onresult nunca dispara — VAD é a fonte de
      // verdade pra "user falando". Cancelar timer pendente enquanto fala.
      if (isIOS()) iosVadStartsRef.current++;
      if (isIOS() && mediaRecorderRef.current?.state === "recording") {
        cancelSilenceTimerFnRef.current?.();
      }
    },
    onVoiceEnd: () => {
      const t0 = voiceTimingsRef.current.t0;
      if (t0) {
        const t5 = performance.now();
        console.log(`[nexo-voice] T5 VAD speech-end (+${(t5 - t0).toFixed(0)}ms)`);
        voiceTimingsRef.current.t5 = t5;
      }
      if (chimesEnabledRef.current && voiceTimingsRef.current.t4) {
        playReceivedChime();
      }
      // FIX iOS STT: VAD onVoiceEnd em iOS arma silence timer pra disparar
      // recorder.stop -> onstop -> POST transcribe -> commitUtterance.
      // Pipeline completo agora funciona sem depender de rec.onresult.
      if (isIOS()) iosVadEndsRef.current++;
      if (isIOS() && mediaRecorderRef.current?.state === "recording") {
        armSilenceTimerFnRef.current?.();
      }
    },
    onBargeIn: () => handleBargeIn(),
  });

  useEffect(() => {
    micApiRef.current.setMicEnabled = setMicEnabled;
    micApiRef.current.setBargeInMode = setBargeInMode;
    // VOICE-FIX-WARMUP — helpers do warmup propagados pro ref.
    micApiRef.current.markSpeakSegmentStart = markSpeakSegmentStart;
    micApiRef.current.clearSpeakSegmentStart = clearSpeakSegmentStart;
    micApiRef.current.getSpeakWarmupRemaining = getSpeakWarmupRemaining;
    // FIX iOS STT (bug 1-linha): getMicTrack faltava aqui — sem isso o
    // startMediaRecorderListen pega undefined e o recorder nunca é criado.
    micApiRef.current.getMicTrack = getMicTrack;
  }, [setMicEnabled, setBargeInMode, markSpeakSegmentStart, clearSpeakSegmentStart, getSpeakWarmupRemaining, getMicTrack]);

  // ====================== PRE-WARM PROACTIVE ======================
  // FIX iOS volume baixo: pre-warm agressivo chama getUserMedia no primeiro
  // gesture do user (clique em qualquer lugar) → iOS audio session vira
  // category playAndRecord → TTS é roteado pelo receiver/earpiece em
  // volume baixo. Em iOS, NÃO registra o listener — mic só ativa em
  // startConversation (line ~1607) com gesto explícito do user.
  // Desktop/Android: comportamento legado preservado (acelera primeira
  // ativação do voice mode em ~300ms).
  useEffect(() => {
    if (isIOS()) {
      console.log("[nexo-voice] iOS detectado — pre-warm do mic adiado pra startConversation");
      return;
    }

    let fired = false;
    const handler = () => {
      if (fired) return;
      fired = true;
      window.removeEventListener("pointerdown", handler, { capture: true });
      window.removeEventListener("keydown", handler, { capture: true });
      ensurePrewarmed?.().catch(() => {});
    };
    window.addEventListener("pointerdown", handler, { capture: true, passive: true });
    window.addEventListener("keydown", handler, { capture: true, passive: true });
    return () => {
      window.removeEventListener("pointerdown", handler, { capture: true });
      window.removeEventListener("keydown", handler, { capture: true });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { overlayOpenRef.current = voiceOverlayOpen; }, [voiceOverlayOpen]);
  useEffect(() => { handleInputRef.current = onVoiceInput; }, [onVoiceInput]);
  useEffect(() => { localStorage.setItem("nexo_voice_enabled", JSON.stringify(voiceEnabled)); }, [voiceEnabled]);
  useEffect(() => { localStorage.setItem("nexo_wakeword_enabled", JSON.stringify(wakeWordEnabled)); }, [wakeWordEnabled]);

  // VOICE-DEBUG-PANEL — cache do permission_state da mic. permissions.query
  // não existe em todos os browsers; falha silenciosa retorna "unknown".
  // Refresh a cada 5s (e na primeira chamada via useEffect abaixo).
  const permissionStateRef = useRef("unknown");
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        if (!navigator?.permissions?.query) {
          permissionStateRef.current = "unsupported";
          return;
        }
        const result = await navigator.permissions.query({ name: "microphone" });
        if (cancelled) return;
        permissionStateRef.current = result.state || "unknown";
        // Listener pra mudanças em tempo real (Chrome suporta).
        if (result.onchange === null) {
          result.onchange = () => {
            permissionStateRef.current = result.state || "unknown";
          };
        }
      } catch (_) {
        permissionStateRef.current = "unknown";
      }
    };
    check();
    const interval = setInterval(check, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // VOICE-INPUT-DEAD — debug helper exposto no console pra diagnosticar
  // drops silenciosos. Rodar `window.__nexoVoiceDebug()` no DevTools.
  // VOICE-DEBUG-PANEL — também consumido pelo VoiceDebugPanel visual.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__nexoVoiceDebug = () => {
      let micTrackEnabled = null;
      let micLabel = null;
      try {
        const track = getMicTrack?.();
        if (track) {
          micTrackEnabled = !!track.enabled;
          micLabel = track.label || "(unnamed)";
        }
      } catch (_) { /* silent */ }
      const lastAt = voiceDropCountRef.current.lastAt;
      return {
        state: voiceState.get(),
        overlay: overlayOpenRef.current,
        locked: pipelineLockedRef.current,
        loading: chatLoadingRef?.current,
        lastTtsEndedMsAgo: lastTtsEndedAtRef.current
          ? Date.now() - lastTtsEndedAtRef.current
          : null,
        inQuiet: isWithinPostTtsQuiet(),
        accFinal: accumulatedFinalRef.current,
        interim: interimTextRef.current,
        recRunning: !!recognitionRef.current,
        recLang: recognitionRef.current?.lang || null,
        recContinuous: recognitionRef.current?.continuous ?? null,
        prewarm: prewarmState,
        voiceOverlayOpen,
        conversationStatus,
        drops: { ...voiceDropCountRef.current },
        lastDropReason: voiceDropCountRef.current.lastReason,
        lastDropAgoMs: lastAt ? Date.now() - lastAt : null,
        micTrackEnabled,
        micLabel,
        permissionState: permissionStateRef.current,
        mediaDevicesSupported: !!(
          typeof navigator !== "undefined" &&
          navigator.mediaDevices?.getUserMedia
        ),
        speechRecognitionSupported: !!(
          typeof window !== "undefined" &&
          (window.SpeechRecognition || window.webkitSpeechRecognition)
        ),
        isSecureContext: typeof window !== "undefined" ? !!window.isSecureContext : null,
        // VOICE-POLISH-V8 — TTS finish reason + stall telemetry.
        lastTtsFinishReason: lastTtsFinishReasonRef.current || "none",
        lastTtsFinishMsAgo: lastTtsFinishAtRef.current
          ? Date.now() - lastTtsFinishAtRef.current
          : null,
        lastStallEmittedAt: typeof window !== "undefined"
          ? (window.__lastStallAt || null)
          : null,
        lastStallText: typeof window !== "undefined"
          ? (window.__lastStallText || null)
          : null,
        // VOICE-FIX-WARMUP — ms restantes do grace period após audio.play().
        // > 0 = barge-in suprimido; 0 = barge-in ativo (ou TTS não tocando).
        speakWarmupRemainingMs:
          typeof micApiRef.current.getSpeakWarmupRemaining === "function"
            ? Math.round(micApiRef.current.getSpeakWarmupRemaining())
            : 0,
        // FIX iOS STT robustez: contadores VAD + status do recorder + último
        // transcribe expostos pro debug panel diagnosticar em tempo real.
        ios_vad_starts: iosVadStartsRef.current,
        ios_vad_ends: iosVadEndsRef.current,
        ios_recorder_state: mediaRecorderRef.current?.state || "null",
        ios_recorder_init: iosRecorderInitStatusRef.current,
        ios_last_transcribe: iosLastTranscribeRef.current,
        last_listen_attempt: lastListenAttemptRef.current,
      };
    };
    return () => {
      try { delete window.__nexoVoiceDebug; } catch (_) {}
    };
  }, [voiceState, chatLoadingRef, prewarmState, voiceOverlayOpen, conversationStatus, getMicTrack]);

  // ====================== HELPERS ======================
  const stopRecognition = useCallback(() => {
    try { recognitionRef.current?.stop(); } catch (_) {}
  }, []);

  const abortRecognition = useCallback(() => {
    // FIX iOS STT: em iOS, stop do MediaRecorder dispara onstop -> Whisper
    // -> commitUtterance. Sem abort path (recognition é stub aqui).
    if (isIOS() && mediaRecorderRef.current?.state === "recording") {
      try { mediaRecorderRef.current.stop(); } catch (_) {}
      return;
    }
    // .abort() drops the in-flight buffer; .stop() preserves it. While TTS is
    // playing we ALWAYS abort so the engine cannot emit a ghost transcript
    // assembled from echo of the assistant itself.
    try { recognitionRef.current?.abort(); } catch (_) {}
  }, []);

  const killTTS = useCallback(() => {
    if (ttsSafetyTimerRef.current) { clearTimeout(ttsSafetyTimerRef.current); ttsSafetyTimerRef.current = null; }
    if (ttsAbortRef.current) { ttsAbortRef.current.abort(); ttsAbortRef.current = null; }
    // FIX iOS Web Audio: para BufferSource ativa (path Web Audio em iOS).
    // Nulificar onended evita disparar finish() duplicado pelo path normal.
    if (currentTTSSourceRef.current) {
      try {
        currentTTSSourceRef.current.onended = null;
        currentTTSSourceRef.current.stop();
        currentTTSSourceRef.current.disconnect();
      } catch (_) {}
      currentTTSSourceRef.current = null;
    }
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch (_) {}
      audioRef.current = null;
    }
    if ("speechSynthesis" in window) { try { speechSynthesis.cancel(); } catch (_) {} }
    // Cancela janela de playback: source matada nunca mais toca.
    ttsAudioPlayingUntilRef.current = 0;
  }, []);

  const canListen = useCallback(() => {
    return overlayOpenRef.current &&
           recognitionRef.current &&
           !pipelineLockedRef.current &&
           !chatLoadingRef?.current;
  }, [chatLoadingRef]);

  // VOICE-INPUT-DEAD fix 4 — registra drop local + dispara toast se
  // 2+ consecutivos (sinal de que algo travou; user precisa feedback).
  // reason: chave estável pra correlação ([voice-drop] <reason>).
  // Reset do consecutive vive em commitUtterance, logo antes do
  // handleInputRef.current(raw).
  const recordDropLocal = useCallback((reason, raw = "") => {
    voiceDropCountRef.current.total += 1;
    voiceDropCountRef.current.consecutive += 1;
    voiceDropCountRef.current.lastReason = reason;
    voiceDropCountRef.current.lastAt = Date.now();
    if (voiceDropCountRef.current.consecutive >= 2) {
      try {
        toast.warning(
          "Não consegui entender. Tente falar mais alto ou reabra a conversa.",
          { id: "voice-drop-warn", duration: 4000 }
        );
      } catch (_) {}
    }
    try { recordVoiceDropRef.current?.(reason, { raw: raw.slice(0, 60) }); } catch (_) {}
    // TELEMETRIA-S1 — persistente em db.execution_traces.
    // raw NUNCA enviado, apenas reason + chars como contexto.
    trace.record("voice.input_dropped", {
      reason,
      chars: raw.length,
    });
  }, []);

  // VOICE-INPUT-DEAD fix H2 — watchdog chatLoadingRef stuck.
  // useJarvisChat pode deixar isLoading=true se um fetch falhar antes
  // do finally (erro de rede, AbortError mal-tratado). Sem este
  // watchdog, canListen() retorna false pra sempre e qualquer fala do
  // user vira "[voice-drop] chat_loading" silencioso. Watchdog mede
  // "tempo contínuo travado em loading=true com overlay aberto" e,
  // se passar de 45s, força reset e retoma listening.
  const chatLoadingStuckSinceRef = useRef(0);
  useEffect(() => {
    if (!chatLoadingRef) return;
    const interval = setInterval(() => {
      if (!overlayOpenRef.current) {
        chatLoadingStuckSinceRef.current = 0;
        return;
      }
      if (!chatLoadingRef.current) {
        chatLoadingStuckSinceRef.current = 0;
        return;
      }
      if (!chatLoadingStuckSinceRef.current) {
        chatLoadingStuckSinceRef.current = Date.now();
        return;
      }
      const stuckMs = Date.now() - chatLoadingStuckSinceRef.current;
      if (stuckMs > 45000) {
        console.warn("[nexo-voice] chat_loading_watchdog_force_reset", { stuck_ms: stuckMs });
        console.log("[voice-drop] watchdog_recover_chat_loading", { stuck_ms: stuckMs });
        try { chatLoadingRef.current = false; } catch (_) {}
        chatLoadingStuckSinceRef.current = 0;
        if (overlayOpenRef.current && !pipelineLockedRef.current) {
          scheduleRestart(200, "watchdog_chat_loading");
        }
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [chatLoadingRef]); // eslint-disable-line react-hooks/exhaustive-deps

  // VOICE-INPUT-DEAD fix H3 — watchdog state machine stuck.
  // SPEAKING/PROCESSING podem ficar pegos indefinidamente se
  // audio.onended nunca disparar E os safety timers existentes
  // (ttsSafetyTimerRef, reListenSafetyTimerRef) falharem por race
  // (clearTimeout no momento errado, closure stale). Watchdog final
  // força IDLE após 35s contínuos em estado não-LISTENING/IDLE com
  // overlay aberto.
  const stateStuckSinceRef = useRef({ state: null, since: 0 });
  useEffect(() => {
    const interval = setInterval(() => {
      if (!overlayOpenRef.current) {
        stateStuckSinceRef.current = { state: null, since: 0 };
        return;
      }
      const current = voiceState.get();
      if (current === STATES.LISTENING || current === STATES.IDLE) {
        stateStuckSinceRef.current = { state: null, since: 0 };
        return;
      }
      const prev = stateStuckSinceRef.current;
      if (prev.state !== current) {
        stateStuckSinceRef.current = { state: current, since: Date.now() };
        return;
      }
      const stuckMs = Date.now() - prev.since;
      if (stuckMs > 35000) {
        console.warn("[nexo-voice] state_machine_watchdog_force_idle", { state: current, stuck_ms: stuckMs });
        console.log("[voice-drop] watchdog_recover_state", { state: current, stuck_ms: stuckMs });
        voiceState.force(STATES.IDLE, "watchdog_state_stuck");
        cancelMicReEnable();
        micApiRef.current.setBargeInMode?.("normal");
        micApiRef.current.setMicEnabled?.(true);
        killTTS();
        stateStuckSinceRef.current = { state: null, since: 0 };
        scheduleRestart(200, "watchdog_state");
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [voiceState, killTTS]); // eslint-disable-line react-hooks/exhaustive-deps

  // ====================== COMMIT (silence-driven) ======================
  // Called when the adaptive silence timer fires. Validates the accumulated
  // text, runs noise/dedup filters, then transitions to PROCESSING.
  const commitUtterance = useCallback(() => {
    // FIX iOS STT: se transcribe in-flight, deixa recorder.onstop completar
    // o ciclo (popular accumulatedFinalRef + chamar commitUtterance recursivo).
    // Sem isso, race: commit roda com accumulatedFinalRef vazio antes do
    // Whisper devolver, e depois onstop dispara duplicado.
    if (isIOS() && transcribePendingRef.current) {
      console.log("[nexo-voice] commitUtterance deferred — iOS transcribe in-flight");
      return;
    }
    const raw = accumulatedFinalRef.current.trim() || interimTextRef.current.trim();
    accumulatedFinalRef.current = "";
    interimTextRef.current = "";
    cancelSilenceTimer();
    if (!raw) return;

    const trimmed = raw.toLowerCase();

    if (trimmed.length < 2) {
      console.log("[voice-drop] too_short", { raw, len: trimmed.length });
      recordDropLocal("too_short", raw);
      return;
    }
    if (/^(nexo|ei nexo|oi nexo|hey nexo|ok nexo)$/i.test(trimmed)) {
      console.log("[voice-drop] wake_word_only", { raw });
      recordDropLocal("wake_word_only", raw);
      return;
    }
    if (chatLoadingRef?.current) {
      console.log("[voice-drop] chat_loading", { raw });
      recordDropLocal("chat_loading", raw);
      return;
    }

    // FIX iOS fantasma: descarta transcript se o TTS BufferSource estava
    // tocando NESTE INSTANTE OU se ainda há uma source viva (gate em
    // startMediaRecorderListen pode falhar se algum trigger fora-do-fluxo
    // iniciou a gravação durante TTS, ou se source.start aconteceu depois
    // do recorder.start por race). Auto-eco do TTS Web Audio NÃO é cancelado
    // pelo AEC do iOS (Web Audio escapa do reference signal do getUserMedia).
    if (ttsAudioPlayingUntilRef.current > Date.now() || currentTTSSourceRef.current) {
      const remaining = Math.max(0, ttsAudioPlayingUntilRef.current - Date.now());
      const sourceAlive = !!currentTTSSourceRef.current;
      console.log("[voice-drop] tts_overlapping_playback", {
        raw: raw.slice(0, 80),
        ms_remaining: remaining,
        source_alive: sourceAlive,
      });
      try {
        trace.record("voice.input_dropped", {
          path: "commit_utterance",
          reason: "tts_overlapping_playback",
          chars: raw.length,
        });
      } catch (_) {}
      recordDropLocal("tts_overlapping_playback", raw);
      return;
    }

    // REFINO-pos-a05eb3a (2026-06-02) — bypass dos 2 filtros (quiet +
    // echo) quando a fala bate exato com uma resposta de clarificação
    // de scope. Frases tipo "a semana inteira"/"tudo"/"só o que falta"
    // sobrepõem com a pergunta do Nexo por design — descartar era hijack.
    const scopeAnswerAllow = isScopeAnswerAllowlisted(raw);
    if (scopeAnswerAllow) {
      console.log("[voice-allow] scope_answer_bypass", { raw: raw.slice(0, 80) });
    }

    // VOICE-UNIFICAR — defesa de timing: descarta qualquer transcript que
    // commit dentro de POST_TTS_QUIET_MS apos o ultimo audio.onended.
    // Speakers tem buffer interno (especialmente bluetooth), e o STT
    // continua captando audio do TTS por algumas centenas de ms apos o
    // audio terminar. Este check pega tudo independente do conteudo.
    if (!scopeAnswerAllow && isWithinPostTtsQuiet()) {
      console.log("[voice-drop] post_tts_quiet", {
        raw: raw.slice(0, 80),
        ms_since_tts_end: Date.now() - lastTtsEndedAtRef.current,
      });
      // Mantemos a tag legado [nexo-voice] dropped_post_tts_quiet abaixo
      // pra compat com scripts/queries existentes — telemetria nova vai
      // via recordVoiceDrop.
      console.log("[nexo-voice] dropped_post_tts_quiet", {
        raw: raw.slice(0, 80),
        ms_since_tts_end: Date.now() - lastTtsEndedAtRef.current,
      });
      recordDropLocal("post_tts_quiet", raw);
      return;
    }

    // VOICE-UNIFICAR — defesa de conteudo: para casos fora da janela de
    // quiet (ex: TTS muito longo e usuario ja podia ter falado) mas com
    // alto overlap com o TTS recente, drop.
    if (!scopeAnswerAllow && isRecentTtsEcho(raw)) {
      console.log("[voice-drop] tts_echo", { raw: raw.slice(0, 80) });
      console.log("[nexo-voice] dropped_tts_echo", { raw: raw.slice(0, 80) });
      recordDropLocal("tts_echo", raw);
      return;
    }

    const now = Date.now();
    if (trimmed === lastSentRef.current.text && now - lastSentRef.current.time < 15000) {
      console.log("[voice-drop] dedup_recent", {
        raw,
        ms_since_last: now - lastSentRef.current.time,
      });
      recordDropLocal("dedup_recent", raw);
      return;
    }

    const isGreeting = /^(oi|ol[áa]|bom dia|boa tarde|boa noite|eae|e a[ií]|fala|hey|opa|tudo bem|blz|beleza)$/i.test(trimmed);
    if (isGreeting && trimmed === lastSentRef.current.text) {
      lastSentRef.current.greetCount++;
      if (lastSentRef.current.greetCount >= 3) {
        console.log("[voice-drop] greet_spam", {
          raw,
          count: lastSentRef.current.greetCount,
        });
        recordDropLocal("greet_spam", raw);
        return;
      }
    } else {
      lastSentRef.current.greetCount = 0;
    }

    lastSentRef.current.text = trimmed;
    lastSentRef.current.time = now;

    cancelPendingRestart();
    abortRecognition();

    // VOICE-C1 F5 — confirm last barge-in event as a true positive when
    // a real transcript follows within the verdict window.
    if (pendingBargeInVerdictRef.current) {
      pendingBargeInVerdictRef.current = false;
      clearBargeInVerdictTimer();
      try { markLastEventFollowUp(true); } catch (_) {}
      console.log("[nexo-voice] barge_in_verdict true_positive");
    }

    voiceState.transition(STATES.PROCESSING, "final_transcript");

    const t0 = voiceTimingsRef.current.t0;
    if (t0) {
      const t6 = performance.now();
      console.log(`[nexo-voice] T6 final transcript ready (+${(t6 - t0).toFixed(0)}ms): ${raw.slice(0, 40)}`);
      // BUGFIX-LATENCY-V2 — paralelo ao [nexo-voice] Tx, prefix [latency]
      // pra agregação. T0→T6 = mic_arm_to_stt_done (Web Speech API +
      // adaptive timer). Não tem request_id ainda — só vai existir quando
      // useJarvisChat disparar a request.
      console.log("[latency] voice.mic_arm_to_stt_done", {
        ms: Math.round(t6 - t0),
        words: raw.trim().split(/\s+/).length,
      });
      voiceTimingsRef.current.t6 = t6;
      // TELEMETRIA-S1 — primeiro transcript pronto pra dispatch.
      // text_len em chars; texto NUNCA enviado.
      trace.record("voice.first_utterance_ready", {
        latency_ms: Math.round(t6 - t0),
        text_len: raw.length,
      });
    }

    // VOICE-INPUT-DEAD fix 4 — commit OK reseta contador consecutivo,
    // mas mantém total acumulado pra __nexoVoiceDebug.
    voiceDropCountRef.current.consecutive = 0;

    if (handleInputRef.current) handleInputRef.current(raw);
  }, [abortRecognition, voiceState, chatLoadingRef, recordDropLocal]);

  // FIX iOS STT: popular refs pros callbacks VAD que rodam ANTES dessa
  // linha (TDZ). cancelSilenceTimer já está no escopo acima (linha 257).
  useEffect(() => {
    cancelSilenceTimerFnRef.current = cancelSilenceTimer;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const armSilenceTimer = useCallback(() => {
    cancelSilenceTimer();
    const wc = countWords(accumulatedFinalRef.current) + countWords(interimTextRef.current);
    // VOICE-C1 F2 — Tiered timeout: rewards longer utterances with more
    // patience (up to 7500ms for 40+ words). V2's linear formula capped at
    // 4000ms, stranding 15+ word pauses.
    const ms = computeTieredSilenceTimeout(wc, silenceBaseRef.current);
    silenceTimerRef.current = setTimeout(() => {
      silenceTimerRef.current = null;
      // FIX iOS STT: em iOS, encerrar gravação dispara recorder.onstop ->
      // Whisper -> commitUtterance. Sem isso o silence timer faria commit
      // direto com accumulatedFinalRef vazio.
      if (isIOS() && mediaRecorderRef.current?.state === "recording") {
        try { mediaRecorderRef.current.stop(); } catch (_) {}
        return;
      }
      commitUtterance();
    }, ms);
  }, [commitUtterance]);

  // FIX iOS STT: popular ref pro callback VAD onVoiceEnd que rodava antes
  // de armSilenceTimer ser declarado.
  useEffect(() => {
    armSilenceTimerFnRef.current = armSilenceTimer;
  }, [armSilenceTimer]);

  // ====================== LOCK / UNLOCK ======================
  const unlockPipeline = useCallback((restartListening = true) => {
    // Force IDLE — escape hatch used by stopSpeaking and external callers.
    if (voiceState.get() !== STATES.IDLE && voiceState.get() !== STATES.LISTENING) {
      voiceState.force(STATES.IDLE, "unlockPipeline");
    }
    if (restartListening && overlayOpenRef.current) {
      // Short delay — caller controls when to actually re-listen via the
      // audio.onended path. This is the legacy hatch; usually the more
      // specific timing is set in speakResponse.finish().
      scheduleRestart(500, "unlockPipeline");
    }
  }, [voiceState]); // eslint-disable-line react-hooks/exhaustive-deps

  // ====================== PUSH-TO-TALK (iOS) ======================
  // Substitui auto-listen em iOS (Whisper alucina em silêncio, AEC do iOS
  // não cancela Web Audio TTS → captura fantasma). Gravação só roda enquanto
  // o user segura o botão; release dispara transcribe + chat.send. Sem VAD,
  // sem silence timer, sem loop de scheduleRestart.
  const startPushToTalk = useCallback(async () => {
    if (pttRecorderRef.current?.state === "recording") {
      console.log("[nexo-voice] PTT já rodando, skip");
      return;
    }

    // Barge-in: para qualquer TTS ativo. O hold-to-talk pressupõe gesto
    // explícito do user, que quer falar AGORA mesmo se o Nexo está respondendo.
    // Sem killTTS, áudio do speaker vazaria pro mic durante a gravação.
    killTTS();

    // Garante mic ativo + warm-up (pré-conexão do getUserMedia que o iOS
    // exige dentro de user gesture; ensurePrewarmed cobre o caso onde o user
    // nunca abriu o overlay antes).
    try { micApiRef.current.setMicEnabled?.(true); } catch (_) {}
    try { await ensurePrewarmed?.(); } catch (_) {}

    const track = micApiRef.current?.getMicTrack?.();
    if (!track) {
      console.error("[nexo-voice] PTT: mic track indisponível");
      try {
        trace.record("voice.input_dropped", {
          path: "push_to_talk",
          reason: "no_mic_track",
        });
      } catch (_) {}
      return;
    }
    const stream = new MediaStream([track]);

    // Mesma cascata de mimeType do startMediaRecorderListen — iOS Safari
    // varia entre webm/opus e mp4 dependendo da versão.
    const CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
    let mime = "";
    for (const c of CANDIDATES) {
      if (window.MediaRecorder?.isTypeSupported?.(c)) { mime = c; break; }
    }

    let recorder;
    try {
      recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
    } catch (e) {
      console.error("[nexo-voice] PTT MediaRecorder constructor falhou:", e);
      try {
        trace.record("voice.input_dropped", {
          path: "push_to_talk",
          reason: "recorder_construct_failed",
          error_class: e?.name || "unknown",
        });
      } catch (_) {}
      return;
    }

    const actualMime = recorder.mimeType || mime || "audio/webm";
    pttRecorderRef.current = recorder;
    pttChunksRef.current = [];
    pttMimeTypeRef.current = actualMime;
    pttHoldStartRef.current = Date.now();
    pttCanceledRef.current = false;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) pttChunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      const chunks = pttChunksRef.current;
      pttChunksRef.current = [];
      const wasCanceled = pttCanceledRef.current;
      pttCanceledRef.current = false;
      pttRecorderRef.current = null;
      setPushToTalkRecording(false);

      if (wasCanceled) {
        console.log("[nexo-voice] PTT recorder.onstop — cancelled, no transcribe");
        try {
          trace.record("voice.input_dropped", {
            path: "push_to_talk",
            reason: "user_cancel",
          });
        } catch (_) {}
        return;
      }

      if (chunks.length === 0) {
        try {
          trace.record("voice.input_dropped", {
            path: "push_to_talk",
            reason: "no_chunks",
          });
        } catch (_) {}
        return;
      }

      const blob = new Blob(chunks, { type: actualMime });
      console.log("[nexo-voice] PTT recorder.onstop blob:", blob.size, "bytes, mime:", actualMime);

      if (blob.size < 1000) {
        try {
          trace.record("voice.input_dropped", {
            path: "push_to_talk",
            reason: "tiny_blob",
            blob_size: blob.size,
          });
        } catch (_) {}
        return;
      }

      try {
        const formData = new FormData();
        const ext = actualMime.includes("mp4") ? "m4a" : "webm";
        formData.append("audio", blob, `ptt.${ext}`);

        const resp = await fetch(`${API_URL}/api/assistant/transcribe`, {
          method: "POST",
          body: formData,
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
        });

        if (!resp.ok) {
          console.error("[nexo-voice] PTT transcribe HTTP", resp.status);
          try {
            trace.record("voice.input_dropped", {
              path: "push_to_talk",
              reason: "transcribe_http_error",
              status: resp.status,
            });
          } catch (_) {}
          return;
        }

        const data = await resp.json();
        const text = (data?.text || "").trim();
        console.log("[nexo-voice] PTT transcribe text:", text, "filtered:", data?.filtered);

        try {
          trace.record("voice.first_utterance_ready", {
            text_len: text.length,
            path: "push_to_talk",
          });
        } catch (_) {}

        if (!text) {
          // Backend (no_speech_prob filter) ou Whisper devolveu vazio.
          try {
            trace.record("voice.input_dropped", {
              path: "push_to_talk",
              reason: data?.filtered === "no_speech" ? "no_speech_filter" : "empty_transcript",
            });
          } catch (_) {}
          return;
        }

        // Direto pro chat — gesto explícito do user, sem filtros de eco/quiet
        // (push-to-talk garante que nada está tocando enquanto grava).
        if (handleInputRef.current) handleInputRef.current(text);
      } catch (err) {
        console.error("[nexo-voice] PTT transcribe falhou:", err);
        try {
          trace.record("voice.input_dropped", {
            path: "push_to_talk",
            reason: "transcribe_exception",
          });
        } catch (_) {}
      }
    };

    recorder.start();
    setPushToTalkRecording(true);
    console.log("[nexo-voice] PTT started — mime:", actualMime);
    try {
      trace.record("voice.conversation_start", {
        path: "push_to_talk",
      });
    } catch (_) {}
  }, [killTTS, ensurePrewarmed, authToken]);

  const stopPushToTalk = useCallback((opts = {}) => {
    const { cancel = false } = opts;
    const recorder = pttRecorderRef.current;
    if (!recorder || recorder.state !== "recording") {
      // setState defensivo: alguma race pode ter deixado a flag UI true
      // mesmo sem recorder ativo. Reset evita botão ficar travado em "Ouvindo".
      setPushToTalkRecording(false);
      return;
    }

    const heldMs = Date.now() - pttHoldStartRef.current;
    const tooShort = heldMs < PTT_MIN_HOLD_MS;

    if (cancel || tooShort) {
      pttCanceledRef.current = true;
      console.log(`[nexo-voice] PTT cancel (heldMs=${heldMs}, tooShort=${tooShort}, explicit=${cancel})`);
    } else {
      console.log(`[nexo-voice] PTT commit (heldMs=${heldMs})`);
    }

    try { recorder.stop(); } catch (_) {}
  }, []);

  // ====================== iOS STT via MediaRecorder ======================
  // Doc DIAGNOSTICO_VOICE_IOS §3: webkitSpeechRecognition é stub em iOS
  // Safari. Path alternativo: capturar mic via MediaRecorder, enviar blob
  // pro backend /api/assistant/transcribe (Whisper-1 pt-BR), popular
  // accumulatedFinalRef com o text retornado e reusar pipeline existente
  // via commitUtterance.
  //
  // DEPRECATED EM iOS (2026-05-29): substituído por push-to-talk
  // (startPushToTalk/stopPushToTalk). Função mantida pra desktop fallback
  // se alguma vez precisar, mas listenOnce iOS branch agora retorna sem
  // chamar isso.
  const startMediaRecorderListen = useCallback(async () => {
    if (mediaRecorderRef.current?.state === "recording") {
      console.log("[nexo-voice] iOS recorder já rodando, skip");
      return;
    }

    // FIX iOS fantasma: se TTS BufferSource ainda está ativo (race do safety
    // timer 8s + decode lento → state foi pra LISTENING prematuramente mas
    // o source.start() rodou depois, ou queue chain entre chunks), defere o
    // recorder até o TTS terminar de verdade + post_tts_quiet. Bloqueia
    // captura de auto-eco quando o voiceState mente sobre o ciclo de fala.
    // Dois sinais: currentTTSSourceRef (BufferSource viva) E ttsAudioPlayingUntilRef
    // (timestamp futuro). Qualquer um true → recusa.
    const playingUntil = ttsAudioPlayingUntilRef.current;
    const sourceAlive = !!currentTTSSourceRef.current;
    if (sourceAlive || playingUntil > Date.now()) {
      const remaining = Math.max(0, playingUntil - Date.now());
      // Se source está viva mas playingUntil expirou ou zerado, usa
      // POST_TTS_QUIET_MS como mínimo pra cobrir o resto do playback.
      const delay = Math.max(remaining, sourceAlive ? 500 : 0) + POST_TTS_QUIET_MS + 100;
      console.log(`[nexo-voice] iOS startMediaRecorderListen adiado ${delay}ms — TTS ativo (source=${sourceAlive}, playingUntil=${remaining}ms)`);
      try {
        trace.record("voice.input_dropped", {
          path: "ios_mediarecorder",
          reason: sourceAlive ? "tts_source_alive" : "tts_audio_playing",
        });
      } catch (_) {}
      if (overlayOpenRef.current) {
        setTimeout(() => listenOnceFnRef.current?.(), delay);
      }
      return;
    }

    try {
      const track = micApiRef.current?.getMicTrack?.();
      if (!track) {
        console.error("[nexo-voice] iOS MediaRecorder: mic track indisponível");
        iosRecorderInitStatusRef.current = "no_track";
        return;
      }
      const stream = new MediaStream([track]);

      let mimeType = "";
      for (const candidate of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
        if (window.MediaRecorder?.isTypeSupported?.(candidate)) {
          mimeType = candidate;
          break;
        }
      }
      // FIX iOS Safari: isTypeSupported retorna false pra todos os candidatos
      // acima, mas new MediaRecorder(stream, {}) (sem mimeType) funciona —
      // browser escolhe codec default (geralmente mp4/aac em iOS). Não dá
      // early return; tenta construir e captura erro real se houver.
      const recorderOptions = mimeType ? { mimeType } : {};

      mediaRecorderChunksRef.current = [];
      let recorder;
      try {
        recorder = new MediaRecorder(stream, recorderOptions);
      } catch (constructErr) {
        console.error("[nexo-voice] iOS MediaRecorder constructor falhou:", constructErr);
        iosRecorderInitStatusRef.current = `ctor_error:${constructErr?.name || "unknown"}`;
        try {
          trace.record("voice.input_dropped", {
            path: "ios_mediarecorder",
            reason: "constructor_error",
            error_class: constructErr?.name,
          });
        } catch (_) {}
        return;
      }

      // Capturar mimeType que o recorder de fato adotou (importante quando
      // isTypeSupported retornou false mas browser escolheu codec próprio).
      const actualMimeType = recorder.mimeType || mimeType || "audio/webm";
      console.log("[nexo-voice] iOS MediaRecorder created with mimeType:", actualMimeType);
      iosRecorderInitStatusRef.current = `ok:${actualMimeType}`;

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) mediaRecorderChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        const chunks = mediaRecorderChunksRef.current;
        mediaRecorderChunksRef.current = [];
        mediaRecorderRef.current = null;

        if (chunks.length === 0) {
          iosLastTranscribeRef.current = { status: "no_chunks", ts: Date.now(), chars: 0 };
          try {
            trace.record("voice.input_dropped", {
              path: "ios_mediarecorder",
              reason: "no_chunks",
            });
          } catch (_) {}
          if (overlayOpenRef.current) scheduleRestart(50, "ios_no_chunks");
          return;
        }

        const blob = new Blob(chunks, { type: actualMimeType });
        console.log("[nexo-voice] iOS recorder.onstop blob:", blob.size, "bytes, mime:", actualMimeType);

        if (blob.size < 1000) {
          iosLastTranscribeRef.current = { status: "tiny_blob", ts: Date.now(), chars: 0 };
          try {
            trace.record("voice.input_dropped", {
              path: "ios_mediarecorder",
              reason: "tiny_blob",
              blob_size: blob.size,
            });
          } catch (_) {}
          if (overlayOpenRef.current) scheduleRestart(50, "ios_tiny_blob");
          return;
        }

        try {
          transcribePendingRef.current = true;
          iosLastTranscribeRef.current = { status: "uploading", ts: Date.now(), chars: 0 };
          const formData = new FormData();
          const ext = actualMimeType.includes("mp4") ? "m4a" : "webm";
          formData.append("audio", blob, `voice.${ext}`);

          const resp = await fetch(`${API_URL}/api/assistant/transcribe`, {
            method: "POST",
            body: formData,
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
          });

          if (!resp.ok) {
            iosLastTranscribeRef.current = { status: `http_${resp.status}`, ts: Date.now(), chars: 0 };
            try {
              trace.record("voice.input_dropped", {
                path: "ios_mediarecorder",
                reason: "transcribe_http_error",
                status: resp.status,
              });
            } catch (_) {}
            console.error("[nexo-voice] iOS transcribe HTTP", resp.status);
            if (overlayOpenRef.current) scheduleRestart(100, "ios_transcribe_http_error");
            return;
          }

          const data = await resp.json();
          const text = (data?.text || "").trim();
          console.log("[nexo-voice] iOS transcribe text:", text);
          iosLastTranscribeRef.current = { status: text ? "ok" : "empty", ts: Date.now(), chars: text.length };

          // TELEMETRIA-S1 — confirmação objetiva pós-deploy.
          try {
            trace.record("voice.first_utterance_ready", {
              text_len: text.length,
              path: "ios_mediarecorder",
            });
          } catch (_) {}

          if (!text) {
            if (overlayOpenRef.current) scheduleRestart(50, "ios_empty_transcript");
            return;
          }

          // FIX iOS hallucination: Whisper inventa subtitle text quando o
          // áudio é silencioso/ruidoso (treinado em legendas YouTube).
          // Filtra hallucinations conhecidas — owner reportou "Legendas
          // pela comunidade Amara.org" aparecendo no chat. Pattern de
          // captura de ECO + áudio quase-silencioso.
          const lower = text.toLowerCase();
          const HALLUCINATIONS = [
            "legendas pela comunidade",
            "amara.org",
            "amara . org",
            "subtitle author",
            "subtitled by",
            "obrigado por assistir",
            "obrigado por ver",
            "thanks for watching",
          ];
          if (HALLUCINATIONS.some((h) => lower.includes(h))) {
            console.log("[nexo-voice] Whisper hallucination filtrado:", text);
            iosLastTranscribeRef.current = { status: "hallucination", ts: Date.now(), chars: text.length };
            try {
              trace.record("voice.input_dropped", {
                path: "ios_mediarecorder",
                reason: "whisper_hallucination",
                chars: text.length,
              });
            } catch (_) {}
            if (overlayOpenRef.current) scheduleRestart(50, "ios_hallucination");
            return;
          }

          accumulatedFinalRef.current = text;
          interimTextRef.current = "";
          transcribePendingRef.current = false;
          commitUtterance();
        } catch (err) {
          iosLastTranscribeRef.current = { status: "exception", ts: Date.now(), chars: 0 };
          console.error("[nexo-voice] iOS transcribe falhou:", err);
          if (overlayOpenRef.current) scheduleRestart(200, "ios_transcribe_exception");
        } finally {
          transcribePendingRef.current = false;
        }
      };

      recorder.start();

      // FIX iOS STT robustez: VAD do usePreWarmedMic depende de noise floor
      // dinâmico que pode ficar acima do nível da voz no iPhone. Sem VAD
      // disparar onVoiceEnd, silence timer não arma → recorder não para.
      // Fallback: força stop após MAX_RECORDING_MS (8s) garantindo Whisper
      // sempre é chamado, mesmo se VAD silenciar.
      const MAX_RECORDING_MS = 8000;
      const fallbackStopTimer = setTimeout(() => {
        if (mediaRecorderRef.current === recorder && recorder.state === "recording") {
          console.log("[nexo-voice] iOS recorder fallback stop after 8s — VAD silent");
          try {
            trace.record("voice.input_dropped", {
              path: "ios_mediarecorder",
              reason: "fallback_timeout",
            });
          } catch (_) {}
          try { recorder.stop(); } catch (_) {}
        }
      }, MAX_RECORDING_MS);

      // Cancela fallback se recorder parar antes (VAD funcionou normalmente)
      recorder.addEventListener("stop", () => clearTimeout(fallbackStopTimer), { once: true });

      voiceState.transition(STATES.LISTENING, "ios_recorder_start");
    } catch (err) {
      console.error("[nexo-voice] startMediaRecorderListen falhou:", err);
      iosRecorderInitStatusRef.current = `outer_error:${err?.name || "unknown"}`;
    }
  }, [voiceState, authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // ====================== LISTEN ONCE ======================
  const listenOnce = useCallback(() => {
    if (!canListen()) {
      const overlay = overlayOpenRef.current;
      const locked = pipelineLockedRef.current;
      const loading = chatLoadingRef?.current;
      const rec = !!recognitionRef.current;
      // Classifica o motivo do skip pra rastreio granular no debug panel.
      const failReason = !overlay ? "overlay_closed"
        : !rec ? "no_recognition"
        : locked ? `locked_${voiceState.get()}`
        : loading ? "chat_loading"
        : "unknown";
      lastListenAttemptRef.current = { where: "listenOnce_canListen", reason: failReason, ts: Date.now(), retries: 0 };
      console.log(`[nexo-voice] listenOnce skipped (reason=${failReason}, overlay=${overlay}, rec=${rec}, locked=${locked}, loading=${loading})`);
      console.log("[voice-drop] listen_skipped", {
        overlay,
        locked,
        loading,
        state: voiceState.get(),
        prewarm: prewarmState,
        rec_exists: rec,
      });
      try {
        trace.record("voice.input_dropped", {
          path: "listen_once_canlisten",
          reason: failReason,
        });
      } catch (_) {}
      recordDropLocal("listen_skipped");
      return;
    }
    accumulatedFinalRef.current = "";
    interimTextRef.current = "";
    cancelSilenceTimer();
    setTranscript("");
    // Make sure mic hardware is on and barge-in mode reset to normal VAD.
    micApiRef.current.setMicEnabled?.(true);
    micApiRef.current.setBargeInMode?.("normal");

    // PUSH-TO-TALK iOS (2026-05-29): auto-listen DESABILITADO em iOS.
    // Whisper alucina ("momento"/"fui"/"obrigado") em silêncio + AEC do
    // iOS não cancela Web Audio TTS → captura fantasma. Em iOS, o mic só
    // grava via gesto explícito de hold (startPushToTalk no ChatInputBar).
    // Mantém state em LISTENING pra UI ficar coerente, mas não cria recorder.
    if (isIOS()) {
      voiceState.transition(STATES.LISTENING, "listen_once_ios_ptt_mode");
      console.log("[nexo-voice] listenOnce iOS skip — push-to-talk mode (hold mic button)");
      try {
        trace.record("voice.input_dropped", {
          path: "listen_once_ios",
          reason: "ios_push_to_talk_mode",
        });
      } catch (_) {}
      return;
    }

    voiceState.transition(STATES.LISTENING, "listen_once");
    try {
      recognitionRef.current.start();
      const t0 = voiceTimingsRef.current.t0;
      if (t0) {
        const t2 = performance.now();
        console.log(`[nexo-voice] T2 SpeechRecognition.start (+${(t2 - t0).toFixed(0)}ms)`);
        voiceTimingsRef.current.t2 = t2;
      }
    } catch (e) {
      const msg = e?.message || String(e);
      const alreadyStarted = msg.includes("already started") || e?.name === "InvalidStateError";
      if (alreadyStarted) {
        console.log(`[nexo-voice] rec.start() race (${e?.name || "msg"}) — scheduling restart`);
        scheduleRestart(400, "start-race");
      } else {
        console.warn(`[nexo-voice] rec.start() failed: ${e?.name || ""} — ${msg}`);
        scheduleRestart(800, "start-error");
      }
    }
  }, [canListen, voiceState, startMediaRecorderListen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { listenOnceFnRef.current = listenOnce; }, [listenOnce]);

  // ====================== TTS ======================
  const playAudioWithRetry = useCallback(async (audio, mySpeakId) => {
    const attempt = async (n) => {
      try {
        await audio.play();
        return true;
      } catch (err) {
        if (mySpeakId !== speakIdRef.current) return false;
        console.warn(`[nexo-voice] audio.play() attempt ${n} failed: ${err.name} — ${err.message}`);
        if (n >= 2) return false;
        await new Promise(r => setTimeout(r, 200));
        if (mySpeakId !== speakIdRef.current) return false;
        return attempt(n + 1);
      }
    };
    return attempt(1);
  }, []);

  /**
   * iOS-only TTS playback via Web Audio API.
   *
   * Bypassa HTMLAudioElement (e autoplay policy do Safari) decodificando o
   * MP3 inteiro em AudioBuffer e tocando via AudioBufferSourceNode. O
   * AudioContext mantém o gesture state uma vez resumido — funciona mesmo
   * 30s+ após o user gesture original.
   *
   * Volume via GainNode (gain=1.0); routing via audioContext.destination.
   *
   * @returns true se tocou; false se falhou decode/start.
   */
  const playTTSViaWebAudio = useCallback(async (arrayBuffer, mySpeakId, onEnded, onError) => {
    const ctx = ttsAudioCtxRef.current;
    if (!ctx) {
      console.error("[nexo-voice] Web Audio path: ttsAudioCtxRef vazio (warmupAudio nunca rodou?)");
      onError?.("no_audio_context");
      return false;
    }
    if (mySpeakId !== speakIdRef.current) return false;
    try {
      if (ctx.state === "suspended") {
        await ctx.resume();
      }
      // FIX iOS fantasma: cancela os 2 safety timers armados em speakResponse
      // (ttsSafetyTimerRef=10s bootstrap + reListenSafetyTimerRef=8s relisten)
      // ANTES do decode. decodeAudioData de chunks grandes (>250 chars) em
      // iPhone pode levar 5-8s; sem cancelar, o timer 8s dispara mid-decode,
      // finish() transiciona state→LISTENING e re-habilita o mic. Quando o
      // decode termina, audio.start() toca SOBRE o mic já hot → Whisper
      // captura eco do TTS + áudio ambiente (TV/rádio) → transcript fantasma.
      if (ttsSafetyTimerRef.current) {
        clearTimeout(ttsSafetyTimerRef.current);
        ttsSafetyTimerRef.current = null;
      }
      cancelReListenSafety();
      // Fallback conservador: se decode+play hangar sem resolver nem rejeitar
      // (caso patológico), depois de 60s force finish. O timer real com
      // a duração do buffer substitui este logo abaixo (caso normal).
      reListenSafetyTimerRef.current = setTimeout(() => {
        console.warn("[nexo-voice] re-listen safety fired (web audio pre-decode 60s) — decode/play hung");
        onEnded?.();
      }, 60000);

      // decodeAudioData consome o ArrayBuffer; slice(0) preserva o original.
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
      if (mySpeakId !== speakIdRef.current) return false;

      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      const gainNode = ctx.createGain();
      gainNode.gain.value = 1.0;
      source.connect(gainNode);
      gainNode.connect(ctx.destination);

      currentTTSSourceRef.current = source;

      source.onended = () => {
        if (mySpeakId !== speakIdRef.current) return;
        try { source.disconnect(); } catch (_) {}
        try { gainNode.disconnect(); } catch (_) {}
        if (currentTTSSourceRef.current === source) currentTTSSourceRef.current = null;
        ttsAudioPlayingUntilRef.current = 0;
        onEnded?.();
      };

      try { micApiRef.current.markSpeakSegmentStart?.(); } catch (_) {}

      // FIX iOS auto-eco fantasma: HARD-STOP do MediaRecorder antes do
      // playback. O AEC do iOS (echoCancellation do getUserMedia) cobre
      // só o caminho de output PADRÃO (HTMLAudioElement). Áudio via Web
      // Audio API NÃO entra no reference signal do AEC, então o speaker
      // vaza pro mic sem cancelamento. A única defesa é o recorder NÃO
      // estar vivo durante TTS. Owner reportou "A TORRES" capturado em
      // ambiente silencioso (só pode ser fragmento do próprio Nexo).
      if (mediaRecorderRef.current?.state === "recording") {
        console.log("[nexo-voice] hard-stop recorder antes de source.start (auto-eco prevention)");
        try {
          trace.record("voice.input_dropped", {
            path: "ios_mediarecorder",
            reason: "tts_hard_stop",
          });
        } catch (_) {}
        try { mediaRecorderRef.current.stop(); } catch (_) {}
      }

      source.start(0);

      // FIX iOS TTS cortado: reseta o reListenSafetyTimer com a duração real
      // do AudioBuffer + 5s margem. Sem isso, TTS de 9s+ era cortado pelo
      // safety timer mesmo tocando OK via Web Audio.
      const durationMs = Math.round(audioBuffer.duration * 1000);
      if (durationMs > 0 && Number.isFinite(durationMs)) {
        cancelReListenSafety();
        const SAFETY_CAP_MS = 300000;
        reListenSafetyTimerRef.current = setTimeout(() => {
          console.warn("[nexo-voice] re-listen safety fired (web audio) — source.onended never came");
          onEnded?.();
        }, Math.min(durationMs + 5000, SAFETY_CAP_MS));
        // Janela de playback ativa — startMediaRecorderListen e commitUtterance
        // consultam pra recusar gravação/commit enquanto o BufferSource roda.
        ttsAudioPlayingUntilRef.current = Date.now() + durationMs;
      }

      const t0 = voiceTimingsRef.current.t0;
      if (t0) {
        const tp = performance.now();
        console.log(`[nexo-voice] T10 Web Audio playing (+${(tp - t0).toFixed(0)}ms) duration=${audioBuffer.duration.toFixed(2)}s`);
      }
      return true;
    } catch (e) {
      console.error("[nexo-voice] Web Audio decode/play falhou:", e);
      onError?.(e?.message || "webaudio_error");
      return false;
    }
  }, []);

  // BUGFIX-LATENCY-V3 — TTS queue ref for chained playback (pre-fetch
  // streaming). speakResponse(text, { queue: true }) appends instead of
  // replacing the current TTS, so the streaming consumer can dispatch
  // the first sentence early and queue the remainder later.
  const ttsQueueRef = useRef([]);
  // Ref to speakResponse itself — finish() closes over this to chain-play
  // queued chunks when the current audio ends, avoiding a circular
  // useCallback dep.
  const speakResponseFnRef = useRef(null);

  const speakResponse = useCallback(async (text, opts = {}) => {
    // V5 — fullContext: texto MAIS completo conhecido no momento (ex: stream
    // acumulado ate aqui). Usado pra alimentar o echo buffer com o contexto
    // total, nao apenas a sentence sendo dispatched. Sem isso, o buffer fica
    // limitado a firstSentence ate o "done" chegar, e ecos de palavras
    // posteriores escapam do filtro de overlap.
    const { queue = false, fullContext = null } = opts;
    if (!text?.trim() || !voiceEnabled) {
      // TELEMETRIA: TTS skip — registra motivo pra desambiguar bug
      // intermitente (1a mensagem OK, 2a não toca). voiceEnabled flipping
      // entre mensagens vs text vazio são causas distintas.
      try {
        trace.record("tts.skip", {
          reason: !text?.trim() ? "empty_text" : "voice_disabled",
          voice_enabled: voiceEnabled,
          text_len: text?.length || 0,
          queue,
        });
      } catch (_) {}
      if (!queue) {
        if (voiceState.get() !== STATES.IDLE && voiceState.get() !== STATES.LISTENING) {
          voiceState.force(STATES.IDLE, "speak_response_skipped");
        }
        if (overlayOpenRef.current) scheduleRestart(50, "speak_skipped");
      }
      return;
    }

    // If we are already speaking and the caller asked to queue, just
    // push and return — the audio.onended → finish() chain will pull
    // from the queue. No second OpenAI request is fired here yet.
    if (queue && voiceState.get() === STATES.SPEAKING) {
      ttsQueueRef.current.push(text);
      rememberTtsText(fullContext || text);
      console.log("[nexo-voice] tts.queue_push", {
        depth: ttsQueueRef.current.length,
        chars: text.length,
      });
      return;
    }

    console.log("[nexo-voice] tts.invoke", {
      text_chars: text.length,
      voice_enabled: voiceEnabled,
    });
    // TELEMETRIA-S1
    trace.record("tts.invoke", { chars: text.length });
    // VOICE-UNIFICAR — record this TTS text in the echo buffer so the next
    // STT transcripts can be filtered against it.
    // V5 — preferir fullContext (stream acumulado) quando disponivel pra
    // capturar palavras alem do firstSentence dispatched.
    rememberTtsText(fullContext || text);

    const mySpeakId = ++speakIdRef.current;
    const t0 = voiceTimingsRef.current.t0;
    const t8 = performance.now();
    if (t0) {
      console.log(`[nexo-voice] T8 /tts request fired (+${(t8 - t0).toFixed(0)}ms)`);
      voiceTimingsRef.current.t8 = t8;
    }

    // Defense-in-depth before audio plays:
    //   1) abort recognition (drop buffered partials → no ghost transcript)
    //   2) hardware-mute mic track (track.enabled = false) — STRICT in C1:
    //      cancel any pending re-enable from a previous TTS that may have
    //      not yet fired, then mute. Guarantees no race-conditioned unmute
    //      during the active SPEAKING segment.
    //   3) switch VAD to "bargein" so only sustained voice fires
    cancelSilenceTimer();
    cancelPendingRestart();
    cancelMicReEnable();
    abortRecognition();
    micApiRef.current.setMicEnabled?.(false);
    micApiRef.current.setBargeInMode?.("bargein");
    killTTS();
    // SPEAKING is reachable from PROCESSING or LISTENING (greeting); both legal.
    voiceState.transition(STATES.SPEAKING, "tts_start");

    const finish = (reason = "unknown") => {
      if (mySpeakId !== speakIdRef.current) return;
      console.log("[nexo-voice] tts.finish_reason", reason);
      // VOICE-POLISH-V8 — track finish reason pro debug panel.
      lastTtsFinishReasonRef.current = reason;
      lastTtsFinishAtRef.current = Date.now();
      // TELEMETRIA-S1 — duração medida do t8 (request fired) até agora.
      try {
        const _t8 = voiceTimingsRef.current.t8;
        const _duration_ms = _t8 ? Math.round(performance.now() - _t8) : null;
        trace.record("tts.finish", {
          reason,
          duration_ms: _duration_ms,
          voice_enabled: voiceEnabled,
          speak_id: mySpeakId,
        });
      } catch (_) { /* silent */ }
      cancelReListenSafety();
      killTTS();

      // BUGFIX-LATENCY-V3 — drain the queue before returning to LISTENING.
      // Pre-fetch streaming pushes the "rest of the text" while the first
      // sentence is still playing; on audio_ended we pop the next chunk
      // and chain-play it (no mic unmute, no state change to LISTENING
      // until the queue is empty).
      const nextChunk = ttsQueueRef.current.shift();
      if (nextChunk && reason === "ended") {
        console.log("[nexo-voice] tts.queue_pop", {
          remaining: ttsQueueRef.current.length, chars: nextChunk.length,
        });
        // V5 — NAO atualiza lastTtsEndedAtRef aqui: TTS continua via
        // queue drain, nao acabou de verdade. Atualizar entre frases fazia
        // o quiet de 3s expirar durante chains longos (3+ sentences),
        // permitindo eco escapar.
        // Stay in SPEAKING; ref-indirection avoids circular useCallback dep.
        // queue=false because we are dequeuing — this call must actually
        // synthesise, not push back onto the queue.
        speakResponseFnRef.current?.(nextChunk, { queue: false });
        return;
      }

      // V5 — TTS REAL terminou (queue vazia ou reason != "ended"). Agora
      // sim marca o quiet period — proximos POST_TTS_QUIET_MS de transcript
      // sao descartados em commitUtterance (defesa contra speaker bleed).
      lastTtsEndedAtRef.current = Date.now();
      // VOICE-FIX-WARMUP — limpa warmup só aqui (queue drenada).
      // No path de drain (nextChunk presente), mantém porque o próximo
      // audio.onplay vai chamar markSpeakSegmentStart() de novo.
      try { micApiRef.current.clearSpeakSegmentStart?.(); } catch (_) {}

      // VOICE-C1 F3 — cancellable re-enable. Keeps the mic muted until 500ms
      // after onended to drain any residual decoder output; cancellable if
      // a new speakResponse() starts mid-drain.
      const reEnableMs = 500;
      scheduleMicReEnable(reEnableMs, `tts_finish:${reason}`);

      // SPEAKING → LISTENING with reason "audio_ended" if we are still in
      // SPEAKING (barge-in already moved us away).
      if (voiceState.get() === STATES.SPEAKING) {
        voiceState.transition(STATES.LISTENING, "audio_ended");
      } else if (voiceState.get() !== STATES.LISTENING) {
        voiceState.force(STATES.IDLE, `finish:${reason}`);
      }

      if (overlayOpenRef.current) {
        scheduleRestart(reEnableMs + 50, `tts_finish:${reason}`);
      }
    };

    ttsSafetyTimerRef.current = setTimeout(() => {
      console.warn("[nexo-voice] TTS bootstrap timeout — audio nunca carregou");
      finish("bootstrap_timeout");
    }, 10000);

    // Fallback re-listen safety — if onended NEVER fires (browser bug, dropped
    // packet etc), do not strand the pipeline in SPEAKING forever.
    cancelReListenSafety();
    reListenSafetyTimerRef.current = setTimeout(() => {
      console.warn("[nexo-voice] re-listen safety fired — audio.onended never came");
      finish("relisten_safety_timeout");
    }, 8000);

    const fetchTtsWithRateLimitRetry = async (body) => {
      const doFetch = async () => {
        const controller = new AbortController();
        ttsAbortRef.current = controller;
        const FETCH_TIMEOUT_MS = Math.max(
          15000,
          Math.min(60000, (body?.text?.length || 0) * 60),
        );
        console.log("[nexo-voice] tts.fetch_timeout_ms", FETCH_TIMEOUT_MS);
        const fetchTimer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
        const headers = { "Content-Type": "application/json" };
        if (authToken) headers.Authorization = `Bearer ${authToken}`;
        try {
          const r = await fetch(`${API_URL}/api/tts`, {
            method: "POST", headers,
            body: JSON.stringify(body),
            signal: controller.signal,
          });
          return r;
        } finally {
          clearTimeout(fetchTimer);
          ttsAbortRef.current = null;
        }
      };
      let r = await doFetch();
      if (r.status === 429 && mySpeakId === speakIdRef.current) {
        console.warn("[nexo-voice] /tts 429 — retrying after 1s");
        await new Promise(rs => setTimeout(rs, 1000));
        if (mySpeakId !== speakIdRef.current) return r;
        r = await doFetch();
      }
      return r;
    };

    try {
      const resp = await fetchTtsWithRateLimitRetry({ text });

      if (mySpeakId !== speakIdRef.current) return;
      if (!resp.ok) throw new Error(`TTS HTTP ${resp.status}`);

      const ct = resp.headers.get("content-type") || "";
      if (ct.includes("audio/")) {
        // FIX iOS chunks grandes: cancela os 2 safety timers (10s bootstrap
        // + 8s relisten) IMEDIATAMENTE após confirmar audio/* da resposta.
        // Antes, o cancel rodava só dentro de playTTSViaWebAudio depois de
        // await ctx.resume() + await arrayBuffer; pra TTS grande (300+ chars,
        // decode 5-8s) o timer 8s disparava ANTES do cancel chegar → finish()
        // marcava SPEAKING como ended prematuramente → race do recorder +
        // playback prolongado (45.6s observado pra speak_id=4 em prod).
        // Aqui sabemos que temos áudio real chegando, então os timers de
        // "audio nunca arrivou" são obsoletos. Arma fallback de 60s que será
        // substituído pelo timer real com a duração do buffer pós-decode.
        if (ttsSafetyTimerRef.current) {
          clearTimeout(ttsSafetyTimerRef.current);
          ttsSafetyTimerRef.current = null;
        }
        cancelReListenSafety();
        reListenSafetyTimerRef.current = setTimeout(() => {
          console.warn("[nexo-voice] re-listen safety fired (post-fetch 60s) — decode/play hung");
          finish("post_fetch_safety_timeout");
        }, 60000);
        // V5 — chunked playback via MediaSource quando suportado e nao
        // for cache hit. Audio.play() comeca com primeiros bytes do MP3
        // em vez de esperar resp.blob() inteiro. Ganho perceptivel:
        // ~2-3s de latencia removidos pra TTS de 50-90 chars.
        const supportsChunked =
          typeof window !== "undefined" &&
          typeof window.MediaSource !== "undefined" &&
          typeof window.MediaSource.isTypeSupported === "function" &&
          window.MediaSource.isTypeSupported("audio/mpeg");
        const cacheHeader = resp.headers.get("X-TTS-Cache") || "";
        const useChunked = supportsChunked && cacheHeader !== "hit" && !!resp.body;

        // Handlers comuns aos dois paths (chunked + blob). Audio recebe
        // sempre os mesmos listeners pra preservar finish() / timings /
        // cancel paths.
        const SAFETY_CAP_MS = 300000;
        const attachAudioHandlers = (audio, cleanupUrl) => {
          audio.onloadedmetadata = () => {
            const durMs = Math.round(audio.duration * 1000);
            console.log("[nexo-voice] tts.audio_duration_ms", durMs);
            // V6 — em MediaSource (chunked), audio.duration === Infinity
            // ate mediaSource.endOfStream() ser chamado. Number.isFinite
            // protege contra NaN/Infinity. Path V4 (blob) sempre tem
            // duration finita aqui.
            const hasFiniteDuration = Number.isFinite(durMs) && durMs > 0;

            // ttsSafetyTimerRef (worst case): cobre os dois paths.
            // hasFiniteDuration: dur + 5s. Sem duration: SAFETY_CAP_MS (300s).
            if (ttsSafetyTimerRef.current) clearTimeout(ttsSafetyTimerRef.current);
            const safetyMs = hasFiniteDuration
              ? Math.min(durMs + 5000, SAFETY_CAP_MS)
              : SAFETY_CAP_MS;
            ttsSafetyTimerRef.current = setTimeout(() => {
              console.warn("[nexo-voice] TTS extended safety — audio passou da duração + 5s");
              finish("safety_timeout_extended");
            }, safetyMs);

            cancelReListenSafety();
            // V6 — relisten apertado SOMENTE quando duration eh finita
            // (path blob V4). Em chunked (duration Infinity inicial), o
            // audio.ondurationchange abaixo arma quando MediaSource
            // .endOfStream() finalizar e duration real ficar conhecida.
            if (hasFiniteDuration) {
              const relistenMs = Math.min(durMs + 1000, SAFETY_CAP_MS);
              reListenSafetyTimerRef.current = setTimeout(() => {
                console.warn("[nexo-voice] re-listen safety fired (extended) — audio.onended never came");
                finish("relisten_safety_extended");
              }, relistenMs);
            }
            // V6 — sem fallback de 8000ms: o ttsSafetyTimerRef de 300s
            // cobre o worst case quando duration nunca for conhecida.
            // O bug V5 era exatamente esse fallback truncando MediaSource
            // antes de endOfStream.
          };

          // V6 — ondurationchange dispara quando MediaSource.endOfStream()
          // finaliza e a duracao real do MP3 fica disponivel. Unico momento
          // confiavel pra armar relisten apertado em chunked playback.
          audio.ondurationchange = () => {
            const durMs = Math.round(audio.duration * 1000);
            if (!Number.isFinite(durMs) || durMs <= 0) return;
            console.log("[nexo-voice] tts.duration_change", durMs);
            // Remaining = duracao total - posicao atual + margem 1s.
            // Se audio.currentTime nao existe ainda, considera = 0
            // (so se onloadedmetadata nao tiver rodado, raro).
            const playedMs = Math.round((audio.currentTime || 0) * 1000);
            const remainingMs = Math.max(0, durMs - playedMs);
            cancelReListenSafety();
            reListenSafetyTimerRef.current = setTimeout(() => {
              console.warn("[nexo-voice] re-listen safety fired (durationchange) — audio.onended never came");
              finish("relisten_safety_durationchange");
            }, Math.min(remainingMs + 1000, SAFETY_CAP_MS));
          };
          audio.onplay = () => {
            // FIX iOS regressão (doc DIAGNOSTICO_VOICE_IOS §2): skip
            // connectAudio em iOS. createMediaElementSource interfere
            // com audio.play() pelo timing do gesture chain — provocava
            // toast "Áudio não tocou". Desktop preserva routing via
            // AudioContext.destination intacto.
            if (!isIOS()) {
              try { connectAudio?.(audio); } catch (e) { console.warn("[nexo-voice] connectAudio falhou:", e); }
            }

            // Dismiss defensivo: toast residual de tentativa anterior pode
            // persistir (sonner não auto-dismissa). Quando audio novo
            // começa a tocar, limpa toasts ativos.
            try { toast.dismiss(); } catch (_) {}

            if (t0) {
              const t10 = performance.now();
              console.log(`[nexo-voice] T10 audio playing (+${(t10 - t0).toFixed(0)}ms)`);
              voiceTimingsRef.current.t10 = t10;
              const t8 = voiceTimingsRef.current.t8;
              if (t8) {
                console.log("[latency] voice.tts_send_to_first_audio_frame", {
                  ms: Math.round(t10 - t8),
                });
                // TELEMETRIA-S1 — latência fetch→first audio frame.
                trace.record("tts.first_audio_frame", {
                  latency_ms: Math.round(t10 - t8),
                });
              }
            }
            // VOICE-FIX-WARMUP — marca início do warmup ANTES da primeira
            // amostra do speaker entrar no analyser. Loop em "bargein"
            // ignora detecção por speakWarmupMs (default 1500ms). Cobre
            // o transiente alto do TTS arrancando que escapa do echo
            // cancellation do built-in speaker.
            try { micApiRef.current.markSpeakSegmentStart?.(); } catch (_) {}
          };
          audio.onended = () => {
            if (!isIOS()) { try { disconnectAudio?.(); } catch (_) {} }
            cleanupUrl();
            finish("ended");
          };
          audio.onerror = (e) => {
            if (!isIOS()) { try { disconnectAudio?.(); } catch (_) {} }
            console.error("[nexo-voice] HTMLAudio error event:", audio.error, e);
            cleanupUrl();
            finish("audio_error");
          };
        };

        // FIX iOS TTS: Web Audio path bypassa HTMLAudioElement + autoplay policy.
        // audio.play() em iOS Safari rejeita quando user gesture já expirou
        // (TTS chega 2-4s pós-Whisper). Web Audio toca via BufferSource sem
        // autoplay — AudioContext mantém gesture state uma vez resumido em
        // warmupAudio. Trade-off: perde streaming chunked, mas iOS ja caia
        // no V4 blob (MediaSource MP3 nao suportado).
        if (isIOS()) {
          try {
            const arrayBuffer = await resp.arrayBuffer();
            if (mySpeakId !== speakIdRef.current) return;
            if (!arrayBuffer || arrayBuffer.byteLength === 0) {
              console.warn("[nexo-voice] /tts retornou arrayBuffer vazio em iOS path");
              try {
                trace.record("voice.input_dropped", {
                  path: "ios_webaudio",
                  reason: "empty_buffer",
                });
              } catch (_) {}
              finish("ios_webaudio_empty_buffer");
              return;
            }
            const ok = await playTTSViaWebAudio(
              arrayBuffer,
              mySpeakId,
              () => finish("ended"),
              (errMsg) => {
                if (mySpeakId !== speakIdRef.current) return;
                console.error("[nexo-voice] Web Audio TTS falhou em iOS:", errMsg);
                try {
                  trace.record("tts.play_failed", {
                    path: "ios_webaudio",
                    speak_id: mySpeakId,
                  });
                } catch (_) {}
                // NÃO cair pra speechSynthesis em iOS — voz nativa tem
                // routing separado em volume baixo + timbre errado.
                toast.error("Áudio indisponível — texto está acima");
                finish(`webaudio_error:${errMsg}`);
              }
            );
            if (!ok && mySpeakId === speakIdRef.current) {
              console.warn("[nexo-voice] playTTSViaWebAudio retornou false sem chamar onError");
            }
            return; // iOS Web Audio path completou — não executa chunked/blob abaixo
          } catch (err) {
            console.error("[nexo-voice] iOS Web Audio path falhou no fetch/buffer:", err);
            if (mySpeakId === speakIdRef.current) {
              toast.error("Áudio indisponível — texto está acima");
              finish("ios_webaudio_fetch_failed");
            }
            return;
          }
        }

        if (useChunked) {
          const mediaSource = new window.MediaSource();
          const url = URL.createObjectURL(mediaSource);
          const audio = new Audio(url);
          audio.preload = "auto";
          // FIX iOS — inline playback (não AVPlayer) + volume explícito.
          audio.playsInline = true;
          audio.setAttribute("webkit-playsinline", "");
          audio.volume = 1.0;
          audioRef.current = audio;

          let revoked = false;
          const cleanupUrl = () => {
            if (revoked) return;
            revoked = true;
            try { URL.revokeObjectURL(url); } catch (_) {}
          };
          attachAudioHandlers(audio, cleanupUrl);

          // Pump chunks from resp.body into MediaSource SourceBuffer.
          // appendBuffer() nao aceita concurrent calls — fila propria
          // controla isso via sourceBuffer.updating + updateend event.
          const pendingChunks = [];
          let sourceBuffer = null;
          let streamEnded = false;

          const tryFlush = () => {
            if (!sourceBuffer || sourceBuffer.updating) return;
            if (pendingChunks.length > 0) {
              try {
                sourceBuffer.appendBuffer(pendingChunks.shift());
              } catch (e) {
                console.error("[nexo-voice] sourceBuffer.appendBuffer failed:", e);
                try { mediaSource.endOfStream("decode"); } catch (_) {}
              }
            } else if (streamEnded && mediaSource.readyState === "open") {
              try { mediaSource.endOfStream(); } catch (_) {}
            }
          };

          mediaSource.addEventListener("sourceopen", () => {
            try {
              sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
              sourceBuffer.addEventListener("updateend", tryFlush);
              tryFlush();
            } catch (e) {
              console.error("[nexo-voice] addSourceBuffer falhou:", e);
              finish("media_source_error");
            }
          });

          const reader = resp.body.getReader();
          (async () => {
            try {
              while (true) {
                const { value, done } = await reader.read();
                if (mySpeakId !== speakIdRef.current) {
                  try { await reader.cancel(); } catch (_) {}
                  return;
                }
                if (done) break;
                if (value && value.byteLength > 0) {
                  pendingChunks.push(value);
                  tryFlush();
                }
              }
              streamEnded = true;
              tryFlush();
            } catch (err) {
              console.error("[nexo-voice] chunked TTS pump failed:", err);
            }
          })();

          const t9 = performance.now();
          if (t0) {
            console.log(`[nexo-voice] T9 audio.play() called CHUNKED (+${(t9 - t0).toFixed(0)}ms)`);
            voiceTimingsRef.current.t9 = t9;
          }

          const ok = await playAudioWithRetry(audio, mySpeakId);
          if (!ok) {
            cleanupUrl();
            if (mySpeakId === speakIdRef.current) {
              console.error("[nexo-voice] audio.play() failed after retry — likely autoplay/policy");
              try {
                trace.record("tts.play_failed", {
                  path: "chunked",
                  speak_id: mySpeakId,
                  duration_ms: t0 ? Math.round(performance.now() - t0) : null,
                });
              } catch (_) {}
              toast.error("Áudio não tocou — texto disponível acima");
              if ("speechSynthesis" in window) {
                try {
                  console.warn("[nexo-voice] tts.fallback_web_speech reason=autoplay_blocked");
                  const u = new SpeechSynthesisUtterance(text);
                  u.lang = "pt-BR"; u.rate = 1.1;
                  u.onend = () => finish("speech_synth_end_after_autoplay_fail");
                  u.onerror = () => finish("speech_synth_error_after_autoplay_fail");
                  speechSynthesis.speak(u);
                  return;
                } catch (_) {}
              }
              finish("autoplay_blocked_no_synth");
            }
          }
          return; // chunked path completou — nao executa o blob path
        }

        // V4 blob path preservado — usado em iOS Safari (MediaSource MP3 nao
        // suportado), em cache hit, e em fallback quando MediaSource crash.
        const blob = await resp.blob();
        if (mySpeakId !== speakIdRef.current) return;
        if (!blob || blob.size === 0) {
          console.warn("[nexo-voice] /tts returned empty blob — falling back to speechSynthesis");
          throw new Error("empty audio blob");
        }
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.preload = "auto";
        // FIX iOS — inline playback (não AVPlayer) + volume explícito.
        audio.playsInline = true;
        audio.setAttribute("webkit-playsinline", "");
        audio.volume = 1.0;
        audioRef.current = audio;

        let revoked = false;
        const cleanupUrl = () => {
          if (revoked) return;
          revoked = true;
          try { URL.revokeObjectURL(url); } catch (_) {}
        };
        attachAudioHandlers(audio, cleanupUrl);

        const t9 = performance.now();
        if (t0) {
          console.log(`[nexo-voice] T9 audio.play() called (+${(t9 - t0).toFixed(0)}ms)`);
          voiceTimingsRef.current.t9 = t9;
        }

        const ok = await playAudioWithRetry(audio, mySpeakId);
        if (!ok) {
          cleanupUrl();
          if (mySpeakId === speakIdRef.current) {
            console.error("[nexo-voice] audio.play() failed after retry — likely autoplay/policy");
            try {
              trace.record("tts.play_failed", {
                path: "v4_blob",
                speak_id: mySpeakId,
                duration_ms: t0 ? Math.round(performance.now() - t0) : null,
              });
            } catch (_) {}
            toast.error("Áudio não tocou — texto disponível acima");
            if ("speechSynthesis" in window) {
              try {
                console.warn("[nexo-voice] tts.fallback_web_speech reason=autoplay_blocked");
                const u = new SpeechSynthesisUtterance(text);
                u.lang = "pt-BR"; u.rate = 1.1;
                u.onend = () => finish("speech_synth_end_after_autoplay_fail");
                u.onerror = () => finish("speech_synth_error_after_autoplay_fail");
                speechSynthesis.speak(u);
                return;
              } catch (_) {}
            }
            finish("autoplay_blocked_no_synth");
          }
        }
      } else {
        const data = await resp.json().catch(() => ({}));
        if (mySpeakId !== speakIdRef.current) return;
        const ttsText = data.text || text;
        if ("speechSynthesis" in window) {
          console.warn("[nexo-voice] tts.fallback_web_speech reason=json_response");
          const u = new SpeechSynthesisUtterance(ttsText);
          u.lang = "pt-BR"; u.rate = 1.1;
          u.onend = () => finish("speech_synth_end_legacy_json");
          u.onerror = () => finish("speech_synth_error_legacy_json");
          speechSynthesis.speak(u);
        } else {
          finish("json_response_no_synth");
        }
      }
    } catch (err) {
      ttsAbortRef.current = null;
      if (mySpeakId !== speakIdRef.current) return;
      console.error("[nexo-voice] /tts pipeline failed:", err.name, err.message);
      if ("speechSynthesis" in window && err.name !== "AbortError") {
        try {
          console.warn("[nexo-voice] tts.fallback_web_speech reason=pipeline_error", err.name);
          const u = new SpeechSynthesisUtterance(text);
          u.lang = "pt-BR"; u.rate = 1.1;
          u.onend = () => finish("speech_synth_end_after_pipeline_error");
          u.onerror = () => finish("speech_synth_error_after_pipeline_error");
          speechSynthesis.speak(u);
          return;
        } catch (_) {}
      }
      finish(err.name === "AbortError" ? "fetch_abort" : "pipeline_error");
    }
  }, [voiceEnabled, killTTS, abortRecognition, voiceState, playAudioWithRetry, playTTSViaWebAudio, authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the function ref in sync — finish() pulls from queue via this.
  useEffect(() => { speakResponseFnRef.current = speakResponse; }, [speakResponse]);

  const stopSpeaking = useCallback(() => {
    killTTS();
    cancelReListenSafety();
    // F3 — kill any pending re-enable timer; we're unmuting now and the
    // ghost timer must not fire later.
    cancelMicReEnable();
    // VOICE-FIX-WARMUP — limpa warmup imediatamente; user cancelou TTS,
    // nada pra proteger no arranque.
    try { micApiRef.current.clearSpeakSegmentStart?.(); } catch (_) {}
    // VOICE-UNIFICAR — same as barge-in: abrupt stop = speaker residue,
    // mark quiet period or STT capture echo of the truncated TTS.
    lastTtsEndedAtRef.current = Date.now();
    ttsQueueRef.current = [];
    micApiRef.current.setBargeInMode?.("normal");
    micApiRef.current.setMicEnabled?.(true);
    voiceState.force(STATES.IDLE, "stop_speaking");
    if (overlayOpenRef.current) scheduleRestart(50, "stop_speaking");
  }, [killTTS, voiceState]); // eslint-disable-line react-hooks/exhaustive-deps

  // ====================== SPEECH RECOGNITION SETUP ======================
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    const rec = new SR();
    // continuous = true: we drive end-of-utterance via the adaptive silence
    // timer in this file, NOT Chrome's heuristic. See computeAdaptiveSilenceTimeout.
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "pt-BR";
    rec.maxAlternatives = 1;
    recognitionRef.current = rec;

    rec.onresult = (event) => {
      // While the pipeline is locked, ignore EVERYTHING — guards against
      // stragglers between rec.abort() and the engine actually stopping.
      if (pipelineLockedRef.current || chatLoadingRef?.current) return;

      let chunkFinal = "";
      let interim = "";
      let maxConfidence = 0;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        const t = r[0].transcript;
        const conf = r[0].confidence || 0;
        if (conf > maxConfidence) maxConfidence = conf;
        if (r.isFinal) chunkFinal += t;
        else interim += t;
      }

      // Final chunks from Chrome are merged into our accumulator, NOT
      // committed. We commit when the silence timer fires.
      if (chunkFinal) {
        // Confidence floor only on final chunks — interims often arrive
        // with conf=0 and we still want them visible.
        if (!(maxConfidence > 0 && maxConfidence < 0.4)) {
          accumulatedFinalRef.current = appendToAccumulator(accumulatedFinalRef.current, chunkFinal);
        }
      }
      interimTextRef.current = interim || "";

      const visible = accumulatedFinalRef.current
        ? `${accumulatedFinalRef.current} ${interimTextRef.current}`.trim()
        : interimTextRef.current;
      setTranscript(visible);

      // Only arm/refresh the timer when there is something to commit.
      if (accumulatedFinalRef.current || interimTextRef.current) {
        armSilenceTimer();
      }
    };

    rec.onerror = (event) => {
      console.log(`[nexo-voice] rec.onerror: ${event.error}`);
      if (event.error === "no-speech") {
        scheduleRestart(2000, "onerror:no-speech");
      } else if (event.error === "not-allowed") {
        toast.error("Permissao de microfone negada");
      }
    };

    rec.onend = () => {
      console.log(`[nexo-voice] rec.onend (locked=${pipelineLockedRef.current}, overlay=${overlayOpenRef.current}, loading=${chatLoadingRef?.current})`);
      if (!pipelineLockedRef.current && overlayOpenRef.current && !chatLoadingRef?.current) {
        scheduleRestart(1500, "onend");
      }
    };

    return () => { try { rec.stop(); } catch (_) {} };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ====================== GREETING ======================
  const greetingAudioUrlRef = useRef(null);
  const playGreetingIfEnabled = useCallback(async () => {
    if (!voiceEnabled) return false;
    // VOICE-INPUT-DEAD fix H1 — greeting é a abertura legítima da
    // sessão; user fala IMEDIATAMENTE depois. NÃO armar post-tts-quiet
    // aqui senão a primeira utterance da sessão é dropada silenciosamente
    // em commitUtterance via isWithinPostTtsQuiet(). O eco filter de
    // turns 2+ continua intacto (speakResponse.finish ainda popula
    // lastTtsEndedAtRef normalmente).
    lastTtsEndedAtRef.current = 0;
    recentTtsRef.current = { text: "", time: 0 };
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const ctl = new AbortController();
      const fetchTimer = setTimeout(() => ctl.abort(), 4000);
      const r = await fetch(`${API_URL}/api/voice/greeting`, { headers, signal: ctl.signal });
      clearTimeout(fetchTimer);
      if (r.status === 204) { console.log("[nexo-voice] greeting disabled (204)"); return false; }
      if (!r.ok) { console.warn(`[nexo-voice] greeting HTTP ${r.status}`); return false; }
      const blob = await r.blob();
      if (!blob || blob.size === 0) { console.warn("[nexo-voice] greeting empty blob"); return false; }
      const url = URL.createObjectURL(blob);
      greetingAudioUrlRef.current = url;
      const audio = new Audio(url);
      audio.preload = "auto";
      // FIX iOS — inline playback + volume explícito.
      audio.playsInline = true;
      audio.setAttribute("webkit-playsinline", "");
      audio.volume = 1.0;

      // During greeting we still mute the mic and run barge-in mode so the
      // user can interrupt the welcome line if they want.
      micApiRef.current.setMicEnabled?.(false);
      micApiRef.current.setBargeInMode?.("bargein");

      await new Promise((resolve) => {
        let done = false;
        const resolveOnce = (why) => {
          if (done) return;
          done = true;
          console.log(`[nexo-voice] greeting resolved: ${why}`);
          if (!isIOS()) { try { disconnectAudio?.(); } catch (_) {} }
          try { URL.revokeObjectURL(url); } catch (_) {}
          resolve();
        };
        // FIX iOS regressão (doc DIAGNOSTICO_VOICE_IOS §2): skip connectAudio em iOS.
        audio.onplay = () => {
          if (!isIOS()) { try { connectAudio?.(audio); } catch (_) {} }
          try { toast.dismiss(); } catch (_) {}
        };
        audio.onended = () => resolveOnce("onended");
        audio.onerror = () => resolveOnce("onerror");
        audio.play().catch((err) => resolveOnce(`play-rejected:${err?.name || err}`));
        setTimeout(() => resolveOnce("timeout"), 3500);
      });

      // Restore mic after greeting; orchestrator will start LISTENING next.
      micApiRef.current.setBargeInMode?.("normal");
      micApiRef.current.setMicEnabled?.(true);
      // VOICE-INPUT-DEAD fix H1 — reforço pós audio.onended/timeout: se
      // alguma race tiver populado lastTtsEndedAtRef nesse meio tempo
      // (improvável mas defensivo), zera de novo antes de devolver
      // controle pro startConversation.
      lastTtsEndedAtRef.current = 0;
      return true;
    } catch (e) {
      console.warn("[nexo-voice] greeting failed:", e.name || "", e.message);
      micApiRef.current.setBargeInMode?.("normal");
      micApiRef.current.setMicEnabled?.(true);
      lastTtsEndedAtRef.current = 0;
      return false;
    }
  }, [voiceEnabled, authToken]);

  // ====================== START / END CONVERSATION ======================
  const startConversation = useCallback(() => {
    const now = Date.now();
    if (overlayOpenRef.current && (now - conversationStartedAtRef.current) < 2000) {
      console.log("[nexo-voice] startConversation ignored — duplicate within 2s");
      return;
    }
    conversationStartedAtRef.current = now;

    const t0 = performance.now();
    voiceTimingsRef.current = { t0 };
    if (typeof window !== "undefined") window.__nexoVoiceT0 = t0;
    console.log("[nexo-voice] T0 mic clicked (start conversation)");
    trace.record("voice.conversation_start", {
      prewarm_state: prewarmState,
    });

    wakeWordSuppressedRef.current = true;
    if (wakeWordRecRef.current) {
      try { wakeWordRecRef.current.stop(); } catch (_) {}
      wakeWordRecRef.current = null;
    }
    setWakeWordListening(false);

    cancelPendingRestart();
    cancelSilenceTimer();
    cancelReListenSafety();
    killTTS();
    abortRecognition();
    accumulatedFinalRef.current = "";
    interimTextRef.current = "";

    overlayOpenRef.current = true;
    setIsConversationActive(true);
    setVoiceOverlayOpen(true);
    setTranscript("");
    lastSentRef.current = { text: "", time: 0, greetCount: 0 };

    voiceState.force(STATES.CONNECTING, "start_conversation");
    const t3 = performance.now();
    console.log(`[nexo-voice] T3 connecting phase (+${(t3 - t0).toFixed(0)}ms)`);
    voiceTimingsRef.current.t3 = t3;

    ensurePrewarmed?.().then(() => {
      const t1 = performance.now();
      console.log(`[nexo-voice] T1 getUserMedia ready (+${(t1 - t0).toFixed(0)}ms)`);
      voiceTimingsRef.current.t1 = t1;
    }).catch(() => {});

    refreshPrefs();

    if (chimesEnabledRef.current) {
      ensureChimesReady();
      try { playListenChime(); } catch (_) {}
    }

    const CONNECTING_DELAY_MS = prewarmState === "prewarmed" ? 250 : 800;
    setTimeout(() => {
      if (!overlayOpenRef.current) return;
      console.log(`[nexo-voice] connecting → listening after ${CONNECTING_DELAY_MS}ms`);

      if (greetingEnabledRef.current) {
        // Greeting plays as a SPEAKING segment so the same gating rules apply
        // (mic muted, barge-in armed). Then we land in LISTENING.
        voiceState.force(STATES.SPEAKING, "greeting_start");
        playGreetingIfEnabled().finally(() => {
          if (!overlayOpenRef.current) return;
          if (voiceState.get() === STATES.SPEAKING) {
            voiceState.transition(STATES.LISTENING, "audio_ended");
          }
          // PTT iOS: não agendar listenOnce (auto-listen desabilitado).
          // Hold-to-talk no botão é o único trigger de gravação.
          if (!isIOS()) scheduleRestart(50, "after-greeting");
        });
      } else {
        voiceState.force(STATES.LISTENING, "no_greeting");
        if (!isIOS()) scheduleRestart(50, "no-greeting");
      }
    }, CONNECTING_DELAY_MS);
  }, [killTTS, abortRecognition, ensurePrewarmed, playGreetingIfEnabled, refreshPrefs, voiceState, prewarmState]); // eslint-disable-line react-hooks/exhaustive-deps

  const endConversation = useCallback(() => {
    console.log("[nexo-voice] endConversation");
    cancelPendingRestart();
    cancelSilenceTimer();
    cancelReListenSafety();
    killTTS();
    abortRecognition();
    // VOICE-FIX-WARMUP — cleanup do warmup ao fechar overlay.
    try { micApiRef.current.clearSpeakSegmentStart?.(); } catch (_) {}
    accumulatedFinalRef.current = "";
    interimTextRef.current = "";
    ttsQueueRef.current = [];
    overlayOpenRef.current = false;
    if (typeof window !== "undefined") window.__nexoVoiceT0 = null;

    micApiRef.current.setBargeInMode?.("normal");
    micApiRef.current.setMicEnabled?.(true);

    setVoiceOverlayOpen(false);
    setIsConversationActive(false);
    voiceState.force(STATES.IDLE, "end_conversation");
    setTranscript("");
    toast("Conversa encerrada");

    if (wakeWordEnabled) {
      setTimeout(() => {
        wakeWordSuppressedRef.current = false;
        startWakeWordListening();
      }, 1500);
    } else {
      wakeWordSuppressedRef.current = false;
    }

    // FIX iOS volume baixo: ao sair do voice mode, libera mic + fecha
    // AudioContext via teardown do usePreWarmedMic. Audio session
    // volta pra category "playback" — TTS subsequente (texto digitado)
    // sai pelo speaker em volume normal, controlado pelo botão de mídia.
    // Desktop/Android: skip (pre-warm é mantido pra latência).
    if (isIOS()) {
      try {
        teardownMic?.();
        console.log("[nexo-voice] iOS — teardown do mic ao sair do voice mode");
      } catch (e) {
        console.warn("[nexo-voice] teardownMic falhou em endConversation:", e);
      }
    }
  }, [killTTS, abortRecognition, voiceState, wakeWordEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // VOICE-UNIFICAR — close the immersive overlay (visual only) WITHOUT
  // killing in-flight TTS. Used when the user closes the overlay mid-speech
  // (ESC, X, backdrop click) — the response must keep playing while the
  // panel becomes visible behind. Audio.onended will still flip
  // SPEAKING → LISTENING normally; nothing else needs to know the overlay
  // is closed except the React state.
  const closeOverlayKeepTTS = useCallback(() => {
    console.log("[nexo-voice] closeOverlayKeepTTS — overlay hidden, TTS continues");
    overlayOpenRef.current = false;
    setVoiceOverlayOpen(false);
    setIsConversationActive(false);
    // Wake word: re-arm asap so the next "Nexo" can re-open the overlay,
    // but only after a short suppression window so the trailing TTS
    // audio doesn't trigger it on its own playback ("Olá Mestre..." would
    // self-wake otherwise).
    if (wakeWordEnabled) {
      setTimeout(() => {
        wakeWordSuppressedRef.current = false;
        startWakeWordListening();
      }, 1500);
    } else {
      wakeWordSuppressedRef.current = false;
    }
  }, [wakeWordEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleVoice = useCallback(() => {
    setVoiceEnabled(prev => {
      const next = !prev;
      // FIX iOS — destrava autoplay + acorda AudioContext DENTRO do user
      // gesture do toggle. Sem isso, o TTS que chegar 2-3s depois é
      // bloqueado pela autoplay policy.
      if (next) warmupAudio();
      if (!next) killTTS();
      toast.success(next ? "Voz ativada" : "Voz desativada");
      return next;
    });
  }, [killTTS, warmupAudio]);

  // ====================== WAKE WORD ======================
  const startWakeWordListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (wakeWordSuppressedRef.current || isConversationActive || voiceOverlayOpen) return;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "pt-BR";
    rec.onstart = () => setWakeWordListening(true);
    rec.onresult = (ev) => {
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript.toLowerCase();
        if (t.includes("nexo")) {
          try { rec.stop(); } catch (_) {}
          wakeWordRecRef.current = null;
          setWakeWordListening(false);
          toast.success("Nexo ativado!", { duration: 1500 });
          startConversation();
          return;
        }
      }
    };
    rec.onerror = () => { setWakeWordListening(false); };
    rec.onend = () => {
      setWakeWordListening(false);
      if (wakeWordSuppressedRef.current) {
        console.log("[nexo-voice] wake word suppressed — not restarting");
        return;
      }
      if (wakeWordEnabled && !isConversationActive && !voiceOverlayOpen) {
        setTimeout(() => {
          if (!wakeWordSuppressedRef.current) startWakeWordListening();
        }, 1000);
      }
    };
    wakeWordRecRef.current = rec;
    try { rec.start(); } catch (_) { setWakeWordListening(false); }
  }, [wakeWordEnabled, isConversationActive, voiceOverlayOpen, startConversation]);

  const toggleWakeWord = useCallback(() => {
    if (wakeWordEnabled) {
      if (wakeWordRecRef.current) { wakeWordRecRef.current.stop(); wakeWordRecRef.current = null; }
      setWakeWordListening(false);
      setWakeWordEnabled(false);
      toast.info("Wake word desativado");
    } else {
      setWakeWordEnabled(true);
      toast.success("Diga 'Nexo' para ativar", { duration: 4000 });
      startWakeWordListening();
    }
  }, [wakeWordEnabled, startWakeWordListening]);

  useEffect(() => {
    if (wakeWordEnabled && !isConversationActive && !wakeWordListening && !voiceOverlayOpen) {
      const t = setTimeout(() => startWakeWordListening(), 1000);
      return () => clearTimeout(t);
    }
  }, [wakeWordEnabled, isConversationActive, wakeWordListening, voiceOverlayOpen, startWakeWordListening]);

  return {
    voiceOverlayOpen, conversationStatus, setConversationStatus,
    transcript, setTranscript, isConversationActive,
    voiceEnabled, wakeWordEnabled, wakeWordListening,
    startConversation, endConversation, stopSpeaking,
    closeOverlayKeepTTS,
    toggleVoice, toggleWakeWord,
    speakResponse,
    // FIX iOS — exposed pra ser chamado no user gesture de outros entry
    // points (handleStartConversation, openChatAttach) que disparam TTS.
    warmupAudio,
    // PUSH-TO-TALK iOS — gesto explícito de hold no botão do mic. Substitui
    // auto-listen contínuo (que falhava com auto-eco + Whisper hallucination).
    startPushToTalk, stopPushToTalk, pushToTalkRecording,
    // Legacy escape hatch — external code can force unlock; prefer the
    // state machine via setConversationStatus(STATES.IDLE).
    unlockPipeline,
    vadSpeaking, prewarmState, isOutputSpeaker,
  };
}
